import { TutorialCategory, DetailedCourse, Course, Lesson } from '../types';
import { courseIconManifest } from './course-icons-manifest';

export const tutorialCategories: TutorialCategory[] = [
    {
        id: 'python',
        name: 'Python',
        description: 'Master Python engineering, from core syntax to advanced concurrency and production patterns.',
        color: '#306998',
        icon: 'Python',
        imageUrl: courseIconManifest['python'],
        courseId: 'python-complete-course',
        mainSlug: 'getting-started-python-basics'
    },
    {
        id: 'html',
        name: 'HTML',
        description: 'Deep-dive into HTML5 architecture, semantic structure, and SEO optimization for modern web engineering.',
        color: '#e34c26',
        icon: 'Html',
        imageUrl: courseIconManifest['html'],
        courseId: 'html-mastery-course',
        mainSlug: 'html-overview'
    },
    {
        id: 'javascript',
        name: 'JavaScript',
        description: 'Beyond the basics: Master asynchronous patterns, memory management, and ES6+ architectural concepts.',
        color: '#f7df1e',
        icon: 'JS',
        imageUrl: courseIconManifest['javascript'],
        courseId: 'js-modern-course',
        mainSlug: 'javascript-introduction'
    },
    {
        id: 'c',
        name: 'C Programming',
        description: 'Master low-level engineering, manual memory architecture, and systems programming with industrial-grade C guides.',
        color: '#a8b9cc',
        icon: 'C',
        imageUrl: courseIconManifest['c'],
        courseId: 'c-fundamentals-course',
        mainSlug: 'c-programming-intro'
    }
];

export const courses: DetailedCourse[] = [
    {
        id: 'python-complete-course',
        title: 'Python Mastery: Complete Beginner to Professional',
        description: 'Master Python engineering from the ground up. This comprehensive course covers everything from core syntax and internal mechanics to advanced concurrency, OOP patterns, and production-grade integrations with FastAPI and Data Science tools.',
        language: 'python',
        chapters: [
            {
                id: 'python-universe',
                title: '1. The Python Universe',
                lessons: [
                    {
                        id: 'python-overview',
                        title: 'Why Python? (The Big Picture)',
                        slug: 'python-overview',
                        contentFile: 'Python/PythonOverview',
                        excerpt: 'Discover why Python is the world\'s most popular language. Learn about its philosophy, versatility in web, AI, and automation, and why it\'s the best starting point for modern engineering.',
                        keywords: ['why learn python', 'python benefits', 'python use cases', 'programming for beginners', 'is python worth learning']
                    },
                    {
                        id: 'python-internals',
                        title: 'Python Internals & CPython',
                        slug: 'python-internals',
                        contentFile: 'Python/PythonInternals',
                        excerpt: 'Go under the hood of Python. Understand the CPython implementation, the compilation process from source code to bytecode, and how the Python Virtual Machine (PVM) executes your code.',
                        keywords: ['python internals', 'cpython architecture', 'python bytecode', 'python virtual machine', 'how python works']
                    },
                    {
                        id: 'python-future',
                        title: 'The Future of Python',
                        slug: 'python-future',
                        contentFile: 'Python/PythonFuture',
                        excerpt: 'Explore the roadmap of Python development. Learn about upcoming features, performance optimizations like the Faster CPython project, and how Python continues to evolve for AI and web scale.',
                        keywords: ['future of python', 'python roadmap', 'faster cpython', 'python 3.13 features', 'python trends']
                    }
                ]
            },
            {
                id: 'python-setup',
                title: '2. Setup & Environment Mastery',
                lessons: [
                    {
                        id: 'python-install',
                        title: 'Clean Installation Guide',
                        slug: 'installation-getting-started',
                        contentFile: 'Python/PythonInstall',
                        excerpt: 'Step-by-step guide to a professional Python installation on Windows, macOS, and Linux. Learn how to manage multiple versions and configure your system path correctly.',
                        keywords: ['install python', 'python setup guide', 'python windows install', 'python mac install', 'python system path']
                    },
                    {
                        id: 'python-virtualenvs',
                        title: 'Virtual Environments (venv/pip)',
                        slug: 'python-virtual-environments',
                        contentFile: 'Python/PythonVirtualEnvs',
                        excerpt: 'Master dependency management with Python virtual environments. Learn why isolation is critical for professional projects and how to use venv and pip effectively.',
                        keywords: ['python virtual environment', 'python venv', 'pip install', 'dependency management', 'project isolation python']
                    },
                    {
                        id: 'python-ide-config',
                        title: 'Professional IDE Setup (VS Code)',
                        slug: 'python-ide-setup',
                        contentFile: 'Python/PythonIDESetup',
                        excerpt: 'Transform VS Code into a powerhouse for Python development. Configure linting, formatting, debugging, and extensions to boost your engineering productivity.',
                        keywords: ['vs code python setup', 'python ide config', 'pylance vscode', 'python debugging vscode', 'coding environment']
                    }
                ]
            },
            {
                id: 'python-core-blocks',
                title: '3. Core Building Blocks',
                lessons: [
                    {
                        id: 'python-syntax',
                        title: 'Syntax & Indentation Rules',
                        slug: 'what-is-syntax',
                        contentFile: 'Python/PythonSyntax',
                        excerpt: 'Master the fundamental rules of Python syntax. Learn why indentation is a core part of the language and how to write clean, readable, and PEP 8 compliant code.',
                        keywords: ['python syntax', 'indentation rules', 'pep 8 basics', 'python coding style', 'python readability']
                    },
                    {
                        id: 'python-comments',
                        title: 'Smart Commenting',
                        slug: 'python-comments',
                        contentFile: 'Python/PythonComments',
                        excerpt: 'Learn the art of documenting your code. Best practices for single-line comments, docstrings, and why meaningful comments are essential for project maintainability.',
                        keywords: ['python comments', 'docstrings', 'writing readable code', 'python documentation', 'best practices comments']
                    },
                    {
                        id: 'python-variables-deep',
                        title: 'Variables & Memory Management',
                        slug: 'python-variables-memory',
                        contentFile: 'Python/PythonVariables',
                        excerpt: 'Understand how Python handles data in memory. Master variable assignment, referencing, and the internal mechanics of how variables point to objects.',
                        keywords: ['python variables', 'memory management', 'object references', 'python memory model', 'variable assignment']
                    },
                    {
                        id: 'python-dynamic-typing',
                        title: 'The Magic of Dynamic Typing',
                        slug: 'python-dynamic-typing',
                        contentFile: 'Python/PythonDynamicTyping',
                        excerpt: 'Explore the flexibility of Python\'s dynamic type system. Learn how types are resolved at runtime and how to use type hinting for better code safety.',
                        keywords: ['dynamic typing python', 'runtime type checking', 'type hinting basics', 'python type system', 'flexible programming']
                    }
                ]
            },
            {
                id: 'python-data-types-deep',
                title: '4. Deep Dive: Data Types',
                lessons: [
                    {
                        id: 'python-data-types-overview',
                        title: 'Data Types Architecture',
                        slug: 'python-data-types',
                        contentFile: 'Python/PythonDataTypes',
                        excerpt: 'Get a bird\'s-eye view of Python\'s built-in data types. Learn about the hierarchy of types, from primitives to complex collections, and how to choose the right one for your data.',
                        keywords: ['python data types', 'primitive types python', 'complex data types', 'type hierarchy', 'choosing data types']
                    },
                    {
                        id: 'python-numbers-precision',
                        title: 'Numbers & Precision Limits',
                        slug: 'python-numbers',
                        contentFile: 'Python/PythonNumbers',
                        excerpt: 'Master numeric operations in Python. Understand the difference between integers, floats, and complex numbers, and learn how to handle precision issues in financial or scientific apps.',
                        keywords: ['python numbers', 'floating point precision', 'integer limits python', 'decimal module', 'math in python']
                    },
                    {
                        id: 'python-casting-internals',
                        title: 'Casting & Conversions',
                        slug: 'python-casting',
                        contentFile: 'Python/PythonCasting',
                        excerpt: 'Learn how to transform data between types safely. Explore explicit and implicit casting, and understand the internal logic Python uses to convert objects without data loss.',
                        keywords: ['python type casting', 'data conversion', 'int to string python', 'float to int', 'explicit casting']
                    },
                    {
                        id: 'python-booleans-logic',
                        title: 'Boolean Algebra in Python',
                        slug: 'python-booleans',
                        contentFile: 'Python/PythonBooleans',
                        excerpt: 'Understand the foundation of logic. Master Python booleans, truthy and falsy values, and how to use boolean algebra to control the flow of your applications.',
                        keywords: ['python booleans', 'truthy vs falsy', 'boolean logic', 'logical flow', 'identity vs equality']
                    }
                ]
            },
            {
                id: 'python-operators-expert',
                title: '5. Operators & Mathematical Precision',
                lessons: [
                    {
                        id: 'python-operators-assignment',
                        title: 'Assignment & Comparison Operators',
                        slug: 'python-operators-assignment',
                        contentFile: 'Python/PythonOperatorsAssignment',
                        excerpt: 'Master the tools of data manipulation. Learn about standard assignment, augmented assignment, and comparison operators that form the basis of decision-making.',
                        keywords: ['python assignment operators', 'comparison operators', 'augmented assignment', 'is vs ==', 'python basic operators']
                    },
                    {
                        id: 'python-operators-arithmetic',
                        title: 'Arithmetic & Precedence',
                        slug: 'python-operators-arithmetic',
                        contentFile: 'Python/PythonOperatorsArithmetic',
                        excerpt: 'Learn the rules of Python arithmetic. Beyond basic math, understand operator precedence (PEMDAS) and the nuances of division and modulo in professional scripts.',
                        keywords: ['python arithmetic', 'operator precedence', 'modular arithmetic', 'integer division', 'math operators python']
                    },
                    {
                        id: 'python-operators-logical',
                        title: 'Logical & Bitwise Operators',
                        slug: 'python-operators-logical',
                        contentFile: 'Python/PythonOperatorsLogical',
                        excerpt: 'Level up your conditional logic. Master logical operators (and, or, not) and dive into bitwise operations for high-performance data processing at the binary level.',
                        keywords: ['python logical operators', 'bitwise operators', 'binary operations python', 'short-circuit evaluation', 'logic gates python']
                    },
                    {
                        id: 'python-walrus',
                        title: 'The Walrus Operator (:=)',
                        slug: 'python-walrus-operator',
                        contentFile: 'Python/PythonWalrus',
                        excerpt: 'Master one of Python\'s most powerful modern features. Learn how the assignment expression operator can simplify your code and improve performance in loops and conditionals.',
                        keywords: ['walrus operator python', 'assignment expression', 'python 3.8 features', 'cleaner loops', 'python optimization']
                    }
                ]
            },
            {
                id: 'python-strings-pro',
                title: '6. Strings & Text Processing',
                lessons: [
                    {
                        id: 'python-strings-basics',
                        title: 'Advanced String Handling',
                        slug: 'python-string-basics',
                        contentFile: 'Python/PythonStringBasics',
                        excerpt: 'Master strings, the most common data type. Learn about unicode support, multi-line strings, indexing, slicing, and essential string methods for professional text processing.',
                        keywords: ['python strings', 'string slicing', 'unicode python', 'string methods', 'text manipulation']
                    },
                    {
                        id: 'python-strings-formatting',
                        title: 'F-Strings & Optimized I/O',
                        slug: 'python-string-format',
                        contentFile: 'Python/PythonStringFormat',
                        excerpt: 'Explore the modern way to format strings. Master f-strings, format(), and why optimized string interpolation is critical for performance-sensitive logging and UI output.',
                        keywords: ['python f-strings', 'string formatting', 'python performance', 'interpolated strings', 'cleaner output python']
                    },
                    {
                        id: 'python-regex-intro',
                        title: 'Intro to Regular Expressions',
                        slug: 'python-regex-intro',
                        contentFile: 'Python/PythonRegex',
                        excerpt: 'Unleash the power of pattern matching. Learn how to use Python\'s re module to validate data, search complex patterns, and automate text parsing like a pro.',
                        keywords: ['python regex', 'regular expressions', 're module python', 'pattern matching', 'data validation']
                    }
                ]
            },
            {
                id: 'python-collections-pro',
                title: '7. Collections (The Engineer\'s Toolset)',
                lessons: [
                    {
                        id: 'python-lists-internals',
                        title: 'Lists: Performance & Internals',
                        slug: 'python-lists-basics',
                        contentFile: 'Python/PythonListsBasics',
                        excerpt: 'Deep dive into Python\'s most versatile collection. Understand dynamic arrays, complexity of list operations (O(1) vs O(n)), and how to write high-performance list code.',
                        keywords: ['python lists', 'dynamic arrays python', 'list performance', 'data structures internals', 'python list operations']
                    },
                    {
                        id: 'python-tuples-packing',
                        title: 'Tuples: Packing & Unpacking',
                        slug: 'python-tuples',
                        contentFile: 'Python/PythonTuples',
                        excerpt: 'Learn why immutability matters. Master tuple packing, unpacking, and why tuples are faster and more memory-efficient than lists for fixed data sets.',
                        keywords: ['python tuples', 'tuple unpacking', 'immutability python', 'tuple vs list', 'python memory efficiency']
                    },
                    {
                        id: 'python-sets-hashing',
                        title: 'Sets: Hashing & Fast Lookups',
                        slug: 'python-sets',
                        contentFile: 'Python/PythonSets',
                        excerpt: 'Master the power of hash tables. Learn how sets provide O(1) membership testing and how to perform complex mathematical set operations like unions and intersections.',
                        keywords: ['python sets', 'hash table basics', 'membership testing', 'set operations python', 'fast data lookups']
                    },
                    {
                        id: 'python-dicts-pro',
                        title: 'Dictionaries: Power & Speed',
                        slug: 'python-dictionaries-basics',
                        contentFile: 'Python/PythonDictsBasics',
                        excerpt: 'The heart of Python. Dive into duck typing, hash maps, and why dictionaries are the engine behind variables, classes, and high-performance data storage.',
                        keywords: ['python dictionaries', 'hash maps python', 'key-value pairs', 'dictionary performance', 'python mapping types']
                    }
                ]
            },
            {
                id: 'python-control-flow-pro',
                title: '8. Control Flow & Optimized Logic',
                lessons: [
                    {
                        id: 'python-if-else-pro',
                        title: 'Advanced Conditional Logic',
                        slug: 'python-if-else',
                        contentFile: 'Python/PythonIfElse',
                        excerpt: 'Go beyond basic if-statements. Learn binary logic, truth value testing, and how to write clean, maintainable conditional structures using logical operators.',
                        keywords: ['python if else', 'conditional logic', 'ternary operator python', 'boolean flow', 'advanced logic']
                    },
                    {
                        id: 'python-loops-iterators',
                        title: 'Loops, Iterators & Range',
                        slug: 'python-for-loops',
                        contentFile: 'Python/PythonForLoops',
                        excerpt: 'Master the art of iteration. Understand how for-loops work with the iterator protocol and how to use the range object for memory-efficient loops.',
                        keywords: ['python for loops', 'iterator protocol', 'range function python', 'looping structures', 'iteration basics']
                    },
                    {
                        id: 'python-while-efficient',
                        title: 'Efficient While Loops',
                        slug: 'python-while-loops',
                        contentFile: 'Python/PythonWhileLoops',
                        excerpt: 'Master conditional looping. Learn best practices for while loops, avoiding infinite loops, and using break, continue, and else clauses effectively.',
                        keywords: ['python while loops', 'conditional iteration', 'loop control', 'break and continue', 'loop best practices']
                    }
                ]
            },
            {
                id: 'python-comprehensions',
                title: '9. Power Features: Comprehensions',
                lessons: [
                    {
                        id: 'python-list-comp',
                        title: 'List Comprehensions',
                        slug: 'python-list-comprehensions',
                        contentFile: 'Python/PythonListComp',
                        excerpt: 'Write elegant, one-line loops. Master the most "Pythonic" feature for creating new lists from existing iterables with filtering and transformations.',
                        keywords: ['python list comprehensions', 'one-line loops', 'pythonic code', 'list filtering', 'efficient list creation']
                    },
                    {
                        id: 'python-dict-set-comp',
                        title: 'Dict & Set Comprehensions',
                        slug: 'python-dict-set-comprehensions',
                        contentFile: 'Python/PythonDictSetComp',
                        excerpt: 'Extend comprehension power to other collections. Learn to build dictionaries and sets dynamically while maintaining readability and performance.',
                        keywords: ['python dict comprehensions', 'set comprehensions', 'advanced comprehensions', 'mapping transformations', 'python efficiency']
                    }
                ]
            },
            {
                id: 'python-functions-deep',
                title: '10. Functions: From Basics to Pro',
                lessons: [
                    {
                        id: 'python-func-architecture',
                        title: 'Function Architecture',
                        slug: 'python-functions',
                        contentFile: 'Python/PythonFunctions',
                        excerpt: 'Build reusable logic. Master function definitions, parameters, return values, and why modular design is the backbone of professional software engineering.',
                        keywords: ['python functions', 'function architecture', 'reusable code', 'modular programming', 'return statements']
                    },
                    {
                        id: 'python-args-kwargs',
                        title: '*args and **kwargs Deep Dive',
                        slug: 'python-function-args',
                        contentFile: 'Python/PythonFunctionArgs',
                        excerpt: 'Handle flexible arguments like a pro. Learn how to design functions that can accept any number of positional and keyword arguments for maximum versatility.',
                        keywords: ['python args kwargs', 'variable arguments', 'flexible functions', 'unpacking arguments', 'advanced function design']
                    },
                    {
                        id: 'python-lambda-closure',
                        title: 'Lambda & Closures',
                        slug: 'python-lambda',
                        contentFile: 'Python/PythonLambda',
                        excerpt: 'Anonymous functions and state persistence. Understand how to use lambda for quick operations and closures to maintain local state across function calls.',
                        keywords: ['python lambda', 'anonymous functions', 'closures python', 'functional programming basics', 'lexical scope']
                    },
                    {
                        id: 'python-scope-legb',
                        title: 'Scope & The LEGB Rule',
                        slug: 'python-scope-legb',
                        contentFile: 'Python/PythonScope',
                        excerpt: 'Never debug a scope error again. Master the LEGB (Local, Enclosing, Global, Built-in) rule and understand how Python resolves variable names.',
                        keywords: ['python scope', 'legb rule', 'global vs local', 'enclosing scope', 'variable resolution']
                    }
                ]
            },
            {
                id: 'python-oop-foundations',
                title: '11. OOP: Building Solid Architectures',
                lessons: [
                    {
                        id: 'python-classes-objects',
                        title: 'Classes, Objects & __init__',
                        slug: 'python-classes-objects',
                        contentFile: 'Python/PythonClasses',
                        excerpt: 'Step into Object-Oriented Programming. Learn how to define classes, instantiate objects, and use the __init__ constructor to build real-world data models.',
                        keywords: ['python classes', 'object oriented programming', 'init method', 'class instantiation', 'data modeling']
                    },
                    {
                        id: 'python-oop-principles',
                        title: 'The 4 Pillars of OOP',
                        slug: 'python-oop-principles',
                        contentFile: 'Python/PythonOOPPillars',
                        excerpt: 'Master the architectural foundations: Encapsulation, Abstraction, Inheritance, and Polymorphism. Learn how to write robust and scalable Python software.',
                        keywords: ['4 pillars of oop', 'encapsulation', 'abstraction', 'inheritance python', 'polymorphism']
                    },
                    {
                        id: 'python-inheritance-mro',
                        title: 'Inheritance & MRO',
                        slug: 'python-inheritance',
                        contentFile: 'Python/PythonInheritance',
                        excerpt: 'Reuse code and handle complexity. Master single and multiple inheritance, and understand the Method Resolution Order (MRO) for complex class hierarchies.',
                        keywords: ['python inheritance', 'multiple inheritance', 'mro python', 'super() method', 'class hierarchies']
                    }
                ]
            },
            {
                id: 'python-oop-pro',
                title: '12. Professional OOP Patterns',
                lessons: [
                    {
                        id: 'python-methods-types',
                        title: 'Class vs Static Methods',
                        slug: 'python-method-types',
                        contentFile: 'Python/PythonMethodTypes',
                        excerpt: 'Understand method decorators. Learn when to use instance methods, @classmethod for factory patterns, and @staticmethod for utility functions within classes.',
                        keywords: ['classmethod vs staticmethod', 'instance methods', 'python method types', 'factory patterns', 'decorator basics']
                    },
                    {
                        id: 'python-properties',
                        title: 'Encapsulation & @property',
                        slug: 'python-properties',
                        contentFile: 'Python/PythonProperties',
                        excerpt: 'The Pythonic way to handle data. Master getters, setters, and deleters using the @property decorator to hide implementation details and protect object state.',
                        keywords: ['python @property', 'encapsulation', 'getters and setters', 'data protection', 'clean oop']
                    },
                    {
                        id: 'python-dunder',
                        title: 'Metaprogramming: Dunder Methods',
                        slug: 'python-dunder-methods',
                        contentFile: 'Python/PythonDunder',
                        excerpt: 'Unlock the magic of double underscores. Learn how __str__, __repr__, __add__, and others allow your custom classes to behave like built-in Python types.',
                        keywords: ['dunder methods', 'python magic methods', 'metaprogramming', 'operator overloading', 'pythonic classes']
                    }
                ]
            },
            {
                id: 'python-advanced-core',
                title: '13. Advanced Core Python',
                lessons: [
                    {
                        id: 'python-decorators',
                        title: 'Decorators: Mastering Wrappers',
                        slug: 'python-decorators',
                        contentFile: 'Python/PythonDecorators',
                        excerpt: 'Extend functionality without modification. Master the syntax and logic of decorators to implement logging, authentication, and timing in a clean, reusable way.',
                        keywords: ['python decorators', 'function wrappers', 'higher order functions', 'decorator patterns', 'clean code decorators']
                    },
                    {
                        id: 'python-generators',
                        title: 'Generators & Memory Savings',
                        slug: 'python-generators',
                        contentFile: 'Python/PythonGenerators',
                        excerpt: 'Handle big data efficiently. Learn how to use yield and generator expressions to process massive datasets without consuming all your system RAM.',
                        keywords: ['python generators', 'yield keyword', 'memory optimization', 'lazy evaluation', 'iterators python']
                    },
                    {
                        id: 'python-context-managers',
                        title: 'Context Managers (with)',
                        slug: 'python-context-managers',
                        contentFile: 'Python/PythonContextManagers',
                        excerpt: 'The "with" statement deep dive. Master resource management and learn how to build your own custom context managers for files, database connections, and locks.',
                        keywords: ['python context managers', 'with statement', 'resource management', 'enter and exit methods', 'safe coding']
                    }
                ]
            },
            {
                id: 'python-system-io',
                title: '14. System, Files & I/O',
                lessons: [
                    {
                        id: 'python-file-io',
                        title: 'Advanced File Handling',
                        slug: 'python-file-handling',
                        contentFile: 'Python/PythonFileHandling',
                        excerpt: 'Master the bridge between memory and disk. Learn to read and write text and binary files, handle encoding, and use buffered I/O for high-performance applications.',
                        keywords: ['python file handling', 'reading writing files', 'binary files python', 'file encoding', 'buffered io']
                    },
                    {
                        id: 'python-data-json',
                        title: 'JSON, CSV & Serialization',
                        slug: 'python-data-serialization',
                        contentFile: 'Python/PythonDataSerialization',
                        excerpt: 'Learn to store and transmit complex data structures. Master JSON and CSV serialization, and understand how to transform objects for web APIs and local storage.',
                        keywords: ['python json serialization', 'csv handling python', 'data persistence', 'json library', 'csv module']
                    },
                    {
                        id: 'python-pathlib',
                        title: 'Pathlib: Modern Path Handling',
                        slug: 'python-pathlib',
                        contentFile: 'Python/PythonPathlib',
                        excerpt: 'Stop using strings for file paths. Learn the modern, object-oriented way to handle paths across different operating systems using the powerful pathlib module.',
                        keywords: ['python pathlib', 'path manipulation', 'cross-platform paths', 'os.path vs pathlib', 'file system navigation']
                    }
                ]
            },
            {
                id: 'python-exceptions-pro',
                title: '15. Exception Handling & Robustness',
                lessons: [
                    {
                        id: 'python-exceptions-logic',
                        title: 'Pro Exception Handling',
                        slug: 'python-exceptions',
                        contentFile: 'Python/PythonExceptions',
                        excerpt: 'Write crash-proof code. Master try-except blocks, handling multiple exceptions, and using finally/else clauses for professional-grade error management.',
                        keywords: ['python exception handling', 'try except finally', 'error management', 'runtime errors', 'exception hierarchy']
                    },
                    {
                        id: 'python-custom-exceptions',
                        title: 'Designing Custom Exceptions',
                        slug: 'python-custom-exceptions',
                        contentFile: 'Python/PythonCustomExceptions',
                        excerpt: 'Create your own error types. Learn how to extend the Exception class to build meaningful and debuggable error structures for large-scale engineering projects.',
                        keywords: ['custom exceptions python', 'raising exceptions', 'exception inheritance', 'error design', 'debugging helpers']
                    }
                ]
            },
            {
                id: 'python-concurrency',
                title: '16. Parallelism & Concurrency',
                lessons: [
                    {
                        id: 'python-threading',
                        title: 'Threading & The GIL',
                        slug: 'python-threading',
                        contentFile: 'Python/PythonThreading',
                        excerpt: 'Understand true parallelism in Python. Master the threading module and learn how the Global Interpreter Lock (GIL) affects your I/O-bound applications.',
                        keywords: ['python threading', 'global interpreter lock', 'gil explained', 'parallelism python', 'io bound tasks']
                    },
                    {
                        id: 'python-asyncio',
                        title: 'Asyncio: Modern Concurrency',
                        slug: 'python-asyncio',
                        contentFile: 'Python/PythonAsyncio',
                        excerpt: 'Master the future of Python concurrency. Learn how to build high-performance, non-blocking applications using the async and await keywords.',
                        keywords: ['python asyncio', 'non-blocking io', 'event loop python', 'async await tutorial', 'concurrent programming']
                    },
                    {
                        id: 'python-multiprocessing',
                        title: 'Multiprocessing for CPU bound',
                        slug: 'python-multiprocessing',
                        contentFile: 'Python/PythonMultiprocessing',
                        excerpt: 'Bypass the GIL for maximum performance. Learn how to use the multiprocessing module to run intensive calculations across all your CPU cores.',
                        keywords: ['python multiprocessing', 'cpu bound tasks', 'parallel processing', 'multi-core python', 'process isolation']
                    }
                ]
            },
            {
                id: 'python-testing',
                title: '17. Testing & Quality Assurance',
                lessons: [
                    {
                        id: 'python-unit-testing',
                        title: 'Unit Testing with Pytest',
                        slug: 'python-testing-basics',
                        contentFile: 'Python/PythonTesting',
                        excerpt: 'Ensure code reliability. Master automated testing with Pytest, including fixtures, parameterized tests, and best practices for Test-Driven Development (TDD).',
                        keywords: ['python unit testing', 'pytest guide', 'automated testing', 'test-driven development', 'pytest fixtures']
                    },
                    {
                        id: 'python-debugging',
                        title: 'Professional Debugging (PDB)',
                        slug: 'python-debugging-pro',
                        contentFile: 'Python/PythonDebugging',
                        excerpt: 'Stop relying on print statements. Master the Python Debugger (pdb) and modern IDE debugging tools to find and fix complex bugs with surgical precision.',
                        keywords: ['python debugging', 'pdb tutorial', 'debugging tools', 'fixing bugs python', 'logic errors']
                    }
                ]
            },
            {
                id: 'python-professional-tooling',
                title: '18. Professional Integration',
                lessons: [
                    {
                        id: 'python-logging',
                        title: 'Logging for Production',
                        slug: 'python-logging',
                        contentFile: 'Python/PythonLogging',
                        excerpt: 'Master production observability. Learn to implement professional logging hierarchies, format outputs, and manage logs for distributed systems.',
                        keywords: ['python logging', 'production logs', 'logging module', 'observability python', 'handling application logs']
                    },
                    {
                        id: 'python-pip-pypi',
                        title: 'Packaging & PyPI',
                        slug: 'python-packaging',
                        contentFile: 'Python/PythonPackaging',
                        excerpt: 'Scale your code to the world. Learn how to package your Python libraries and publish them to the Python Package Index (PyPI) for others to use.',
                        keywords: ['python packaging', 'pypi publishing', 'setup.py vs pyproject.toml', 'creating python libraries', 'pip publishing']
                    }
                ]
            },
            {
                id: 'python-modern-web',
                title: '19. Python for the Modern Web',
                lessons: [
                    {
                        id: 'python-web-apis',
                        title: 'Building APIs with FastAPI',
                        slug: 'python-fastapi-intro',
                        contentFile: 'Python/PythonFastAPI',
                        excerpt: 'Build high-performance web backends. Learn the foundations of FastAPI, including pydantic models, automatic documentation, and asynchronous endpoints.',
                        keywords: ['fastapi tutorial', 'python web apis', 'restful backend', 'async web python', 'pydantic models']
                    },
                    {
                        id: 'python-requests',
                        title: 'Consuming APIs with Requests',
                        slug: 'python-requests-lib',
                        contentFile: 'Python/PythonRequests',
                        excerpt: 'Interact with the modern web. Master the Requests library to send HTTP requests, handle JSON responses, and integrate with external third-party APIs.',
                        keywords: ['python requests library', 'consuming apis', 'http requests python', 'json api interaction', 'python web services']
                    }
                ]
            },
            {
                id: 'python-data-science-intro',
                title: '20. Intro to Data Science',
                lessons: [
                    {
                        id: 'python-pandas-numpy',
                        title: 'Numpy & Pandas Foundation',
                        slug: 'python-data-science-basics',
                        contentFile: 'Python/PythonDataScience',
                        excerpt: 'The gateway to AI and Data Science. Learn the fundamentals of Numpy for vectorization and Pandas for high-performance data manipulation and analysis.',
                        keywords: ['python data science', 'numpy basics', 'pandas tutorial', 'data analysis python', 'scientific computing']
                    }
                ]
            }
        ]
    },
    {
        id: 'html-mastery-course',
        title: 'HTML5 Mastery: The Complete Web Foundation',
        description: 'Build a rock-solid foundation for web development. Master HTML5 from basic document structure and semantic tags to advanced SEO, schema markup, and modern accessibility standards.',
        language: 'html',
        chapters: [
            {
                id: 'html-foundations',
                title: '1. HTML Foundations & History',
                lessons: [
                    {
                        id: 'html-overview',
                        title: 'HTML: The Big Picture',
                        slug: 'html-overview',
                        contentFile: 'HTML/HTMLOverview',
                        excerpt: 'Understand the big picture of HTML5. Learn how the HyperText Markup Language forms the structural foundation of every website on the internet.',
                        keywords: ['what is html', 'html5 overview', 'web structure basics', 'frontend foundations', 'how websites work']
                    },
                    {
                        id: 'html-history',
                        title: 'Evolution of HTML (HTML1 to HTML5)',
                        slug: 'html-history',
                        contentFile: 'HTML/HTMLHistory',
                        excerpt: 'Trace the evolution of the web from HTML1 to the modern HTML5 Living Standard. Understand the role of the W3C and WHATWG in shaping the internet.',
                        keywords: ['html history', 'evolution of html', 'html5 vs html4', 'w3c whatwg', 'web standards history']
                    },
                    {
                        id: 'html-syntax',
                        title: 'HTML Syntax & Document Structure',
                        slug: 'html-syntax',
                        contentFile: 'HTML/HTMLSyntax',
                        excerpt: 'Master the fundamental syntax of HTML. Learn about tag structures, element nesting rules, and how to write clean, valid markup code.',
                        keywords: ['html syntax', 'html tags', 'nesting elements', 'closing tags', 'markup language basics']
                    },
                    {
                        id: 'html-elements',
                        title: 'Elements & Attributes Deep Dive',
                        slug: 'html-elements',
                        contentFile: 'HTML/HTMLElements',
                        excerpt: 'Deep dive into HTML elements and attributes. Understand the difference between block-level and inline elements and how to use global attributes.',
                        keywords: ['html elements', 'html attributes', 'block vs inline', 'global attributes', 'nesting rules html']
                    }
                ]
            },
            {
                id: 'html-document-structure',
                title: '2. Document Architecture',
                lessons: [
                    {
                        id: 'html-doctype',
                        title: 'DOCTYPE & HTML Declaration',
                        slug: 'html-doctype',
                        contentFile: 'HTML/HTMLDoctype',
                        excerpt: 'Learn why the DOCTYPE declaration is critical for browser rendering. Understand quirks mode vs standard mode and the history of document types.',
                        keywords: ['html doctype', 'declaration html', 'quirks mode', 'standard mode', 'rendering engines']
                    },
                    {
                        id: 'html-head',
                        title: 'The <head> Section Mastery',
                        slug: 'html-head',
                        contentFile: 'HTML/HTMLHead',
                        excerpt: 'Master the invisible engine of your webpage: the <head> section. Learn how to manage titles, character sets, and base URLs for better site control.',
                        keywords: ['html head section', 'charset utf-8', 'page title seo', 'base url html', 'head elements']
                    },
                    {
                        id: 'html-metadata',
                        title: 'Meta Tags & SEO Optimization',
                        slug: 'html-metadata',
                        contentFile: 'HTML/HTMLMetadata',
                        excerpt: 'Strategic meta tag engineering for SEO and social sharing. Learn to use descriptions, keywords, and author tags to improve search engine visibility.',
                        keywords: ['html meta tags', 'seo meta tags', 'meta description', 'viewport meta', 'robots meta']
                    },
                    {
                        id: 'html-body',
                        title: 'The <body> Section',
                        slug: 'html-body',
                        contentFile: 'HTML/HTMLBody',
                        excerpt: 'Inside the <body> section. Learn how to structure the visible content of your webpage using semantic containers and modern architectural patterns.',
                        keywords: ['html body tag', 'visible content structure', 'document body', 'semantic containers', 'page architecture']
                    }
                ]
            },
            {
                id: 'html-text-content',
                title: '3. Text Content & Typography',
                lessons: [
                    {
                        id: 'html-headings',
                        title: 'Headings Hierarchy (H1-H6)',
                        slug: 'html-headings',
                        contentFile: 'HTML/HTMLHeadings',
                        excerpt: 'Master the SEO heading hierarchy. Learn why using H1-H6 tags correctly is vital for accessibility, readability, and search engine ranking.',
                        keywords: ['html headings', 'h1-h6 hierarchy', 'heading seo', 'accessibility headings', 'content structure html']
                    },
                    {
                        id: 'html-paragraphs',
                        title: 'Paragraphs & Text Formatting',
                        slug: 'html-paragraphs',
                        contentFile: 'HTML/HTMLParagraphs',
                        excerpt: 'Formatting text with paragraphs and line breaks. Learn the semantic difference between <p> and <br> and how to handle white space in HTML.',
                        keywords: ['html paragraphs', 'line breaks html', 'p tag', 'br tag', 'text formatting html']
                    },
                    {
                        id: 'html-inline-text',
                        title: 'Inline Text Elements (span, strong, em)',
                        slug: 'html-inline-text',
                        contentFile: 'HTML/HTMLInlineText',
                        excerpt: 'Semantic emphasis and text markup. Understand the difference between strong, em, span, and other inline elements for precise content meaning.',
                        keywords: ['html inline elements', 'strong vs b', 'em vs i', 'span tag', 'text emphasis html']
                    },
                    {
                        id: 'html-lists',
                        title: 'Lists: Ordered, Unordered, Description',
                        slug: 'html-lists',
                        contentFile: 'HTML/HTMLLists',
                        excerpt: 'Organizing data with ordered, unordered, and description lists. Learn to group content logically and nest lists for complex data structures.',
                        keywords: ['html lists', 'ordered lists ol', 'unordered lists ul', 'description lists dl', 'nesting lists html']
                    },
                    {
                        id: 'html-quotes',
                        title: 'Quotations & Citations',
                        slug: 'html-quotes',
                        contentFile: 'HTML/HTMLQuotes',
                        excerpt: 'Proper quotation and citation markup. Learn how to use blockquote, q, and cite tags to attribute sources correctly and improve SEO.',
                        keywords: ['html quotations', 'blockquote tag', 'q tag', 'citation markup', 'web citations']
                    }
                ]
            },
            {
                id: 'html-links-navigation',
                title: '4. Links & Navigation',
                lessons: [
                    {
                        id: 'html-anchors',
                        title: 'Anchor Tags & Hyperlinks',
                        slug: 'html-anchors',
                        contentFile: 'HTML/HTMLAnchors',
                        excerpt: 'Master the power of hyperlinks. Learn about anchor tags, href attributes, and how to use the rel attribute for security and performance.',
                        keywords: ['html links', 'anchor tags', 'hyperlink basics', 'rel noopener', 'target blank']
                    },
                    {
                        id: 'html-urls',
                        title: 'URL Structure: Absolute vs Relative',
                        slug: 'html-urls',
                        contentFile: 'HTML/HTMLUrls',
                        excerpt: 'Absolute vs Relative URLs explained. Learn how to structure file paths correctly to avoid broken links and improve site maintainability.',
                        keywords: ['absolute vs relative urls', 'file paths html', 'linking files', 'url structure', 'broken links fix']
                    },
                    {
                        id: 'html-navigation',
                        title: 'Building Navigation Menus',
                        slug: 'html-navigation',
                        contentFile: 'HTML/HTMLNavigation',
                        excerpt: 'Building semantic navigation structures. Learn to use the <nav> element to create accessible and SEO-friendly site menus.',
                        keywords: ['html navigation', 'nav tag', 'semantic menus', 'accessible navigation', 'website menus']
                    },
                    {
                        id: 'html-bookmarks',
                        title: 'Page Anchors & Bookmarks',
                        slug: 'html-bookmarks',
                        contentFile: 'HTML/HTMLBookmarks',
                        excerpt: 'Internal page fragment navigation. Learn how to use ID attributes to create smooth bookmarks and jump-to-section links within a single page.',
                        keywords: ['html bookmarks', 'anchor links', 'jump to section', 'internal links', 'id attributes link']
                    }
                ]
            },
            {
                id: 'html-images-graphics',
                title: '5. Images & Graphics',
                lessons: [
                    {
                        id: 'html-images',
                        title: 'Image Tag & Attributes',
                        slug: 'html-images',
                        contentFile: 'HTML/HTMLImages',
                        excerpt: 'The engine of visual web content. Master <img> tag attributes, alternative text for SEO, and how browsers fetch and render image files.',
                        keywords: ['html images', 'img tag', 'alt text seo', 'image attributes', 'web images guide']
                    },
                    {
                        id: 'html-image-formats',
                        title: 'Image Formats (JPG, PNG, SVG, WebP)',
                        slug: 'html-image-formats',
                        contentFile: 'HTML/HTMLImageFormats',
                        excerpt: 'Choosing the right image format. Compare WebP, SVG, PNG, and JPG to optimize performance and visual quality for modern web browsers.',
                        keywords: ['webp vs svg', 'image formats web', 'png vs jpg', 'optimizing images', 'modern image extensions']
                    },
                    {
                        id: 'html-responsive-images',
                        title: 'Responsive Images (srcset, picture)',
                        slug: 'html-responsive-images',
                        contentFile: 'HTML/HTMLResponsiveImages',
                        excerpt: 'Responsive image engineering. Master srcset and <picture> tags to serve perfectly sized images for every device, from mobile to desktop.',
                        keywords: ['responsive images html', 'srcset attribute', 'picture element', 'art direction web', 'mobile image optimization']
                    },
                    {
                        id: 'html-image-maps',
                        title: 'Image Maps & Clickable Areas',
                        slug: 'html-image-maps',
                        contentFile: 'HTML/HTMLImageMaps',
                        excerpt: 'Creating interactive clickable image areas. Learn how to use <map> and <area> tags to define regions within a single image for navigation.',
                        keywords: ['html image maps', 'clickable images', 'area tag html', 'map tag', 'interactive images']
                    },
                    {
                        id: 'html-svg',
                        title: 'Inline SVG & Vector Graphics',
                        slug: 'html-svg',
                        contentFile: 'HTML/HTMLSvg',
                        excerpt: 'Vector graphics for the modern web. Learn to integrate Scalable Vector Graphics (SVG) directly into your HTML for crystal-clear icons and illustrations.',
                        keywords: ['html svg', 'vector graphics web', 'inline svg', 'svg vs icon font', 'scalable vectors']
                    },
                    {
                        id: 'html-canvas',
                        title: 'Canvas Element Basics',
                        slug: 'html-canvas',
                        contentFile: 'HTML/HTMLCanvas',
                        excerpt: 'The foundation of browser-based graphics. Learn how the <canvas> element provides a scriptable area for 2D and 3D rendering and games.',
                        keywords: ['html5 canvas', 'canvas element basics', '2d graphics web', 'scriptable graphics', 'rendering pixel data']
                    }
                ]
            },
            {
                id: 'html-multimedia',
                title: '6. Audio & Video',
                lessons: [
                    {
                        id: 'html-audio',
                        title: 'Audio Element & Controls',
                        slug: 'html-audio',
                        contentFile: 'HTML/HTMLAudio',
                        excerpt: 'Audio integration for the modern web. Learn how to use the <audio> element to provide high-quality audio playback and control options to your users.',
                        keywords: ['html audio tag', 'mp3 on web', 'audio controls html', 'browser audio playback', 'web multimedia']
                    },
                    {
                        id: 'html-video',
                        title: 'Video Element & Formats',
                        slug: 'html-video',
                        contentFile: 'HTML/HTMLVideo',
                        excerpt: 'Video streaming and control attributes. Master the <video> tag, including source selection, preload strategies, and cross-browser video support.',
                        keywords: ['html video tag', 'web video formats', 'video streaming html', 'source tag html', 'mp4 web guide']
                    },
                    {
                        id: 'html-media',
                        title: 'Media Optimization & Best Practices',
                        slug: 'html-media',
                        contentFile: 'HTML/HTMLMedia',
                        excerpt: 'Best practices for web media optimization. Learn to balance quality and performance with proper encoding, lazy loading, and professional media delivery.',
                        keywords: ['multimedia optimization', 'lazy loading media', 'web performance multimedia', 'media best practices', 'frontend media engineering']
                    },
                    {
                        id: 'html-iframe',
                        title: 'Embedding Content (iframe, embed, object)',
                        slug: 'html-iframe',
                        contentFile: 'HTML/HTMLIframe',
                        excerpt: 'Embedding third-party content securely. Master <iframe> attributes like sandbox and loading, and learn how to integrate external widgets and dashboards.',
                        keywords: ['html iframe tag', 'embedding content web', 'iframe security sandbox', 'web components embed', 'object tag html']
                    }
                ]
            },
            {
                id: 'html-tables',
                title: '7. Tables & Data Presentation',
                lessons: [
                    {
                        id: 'html-table-basics',
                        title: 'Table Structure (table, tr, td, th)',
                        slug: 'html-table-basics',
                        contentFile: 'HTML/HTMLTableBasics',
                        excerpt: 'Semantic table structures for data presentation. Learn the core tags (table, tr, td, th) and how to build well-structured, readable data tables.',
                        keywords: ['html table structure', 'tr td th tags', 'simple tables html', 'data presentation web', 'table formatting']
                    },
                    {
                        id: 'html-table-advanced',
                        title: 'Advanced Tables (colspan, rowspan, caption)',
                        slug: 'html-table-advanced',
                        contentFile: 'HTML/HTMLTableAdvanced',
                        excerpt: 'Master advanced table mechanics. Learn to span multiple rows and columns with colspan and rowspan, and use captions for better data context.',
                        keywords: ['colspan html', 'rowspan html', 'complex tables web', 'table caption tag', 'advanced table layout']
                    },
                    {
                        id: 'html-table-styling',
                        title: 'Table Accessibility & Semantic Tables',
                        slug: 'html-table-styling',
                        contentFile: 'HTML/HTMLTableStyling',
                        excerpt: 'Building accessible and professional tables. Learn to use thead, tbody, and tfoot tags, and implement scope attributes for screen reader clarity.',
                        keywords: ['accessible tables html', 'thead tbody tfoot', 'scope attribute html', 'semantic tables', 'a11y for tables']
                    }
                ]
            },
            {
                id: 'html-forms',
                title: '8. Forms & Input Mastery',
                lessons: [
                    {
                        id: 'html-form-basics',
                        title: 'Form Structure & Methods (GET, POST)',
                        slug: 'html-form-basics',
                        contentFile: 'HTML/HTMLFormBasics',
                        excerpt: 'Core architecture of web forms. Learn how to use <form> attributes, action paths, and understand the practical differences between GET and POST methods.',
                        keywords: ['html form basics', 'get vs post method', 'form action attribute', 'input grouping', 'user input architecture']
                    },
                    {
                        id: 'html-input-types',
                        title: 'Input Types (text, email, password, number)',
                        slug: 'html-input-types',
                        contentFile: 'HTML/HTMLInputTypes',
                        excerpt: 'Deep dive into HTML5 input types. Learn how choosing the right input type improves user experience on mobile and provides automatic validation.',
                        keywords: ['html5 input types', 'email input mobile', 'number input web', 'password field security', 'form optimization mobile']
                    },
                    {
                        id: 'html-form-elements',
                        title: 'Form Elements (textarea, select, button)',
                        slug: 'html-form-elements',
                        contentFile: 'HTML/HTMLFormElements',
                        excerpt: 'Complex user input elements. Learn how to implement textareas, dropdown selects, and professional buttons for a rich, interactive user experience.',
                        keywords: ['html select dropdown', 'textarea auto-resize', 'button types web', 'form controls guide', 'interactive forms']
                    },
                    {
                        id: 'html-forms',
                        title: 'Form Validation & Attributes',
                        slug: 'html-forms',
                        contentFile: 'HTML/HTMLForms',
                        excerpt: 'Client-side validation and form attributes. Master the required, pattern, and title attributes to provide immediate feedback and catch errors early.',
                        keywords: ['html5 form validation', 'required attribute html', 'input patterns regex', 'custom error messages types', 'frontend validation logic']
                    },
                    {
                        id: 'html-form-advanced',
                        title: 'Advanced Forms (fieldset, datalist, output)',
                        slug: 'html-form-advanced',
                        contentFile: 'HTML/HTMLFormAdvanced',
                        excerpt: 'Specialized elements for professional forms. Learn to use fieldset and legend for grouping, and datalist for smart-suggesting user inputs.',
                        keywords: ['html fieldset legend', 'datalist input suggest', 'output tag html5', 'advanced form architecture', 'user experience forms']
                    }
                ]
            },
            {
                id: 'html-semantic',
                title: '9. Semantic HTML5',
                lessons: [
                    {
                        id: 'html-semantic-intro',
                        title: 'Why Semantic HTML Matters',
                        slug: 'html-semantic-intro',
                        contentFile: 'HTML/HTMLSemanticIntro',
                        excerpt: 'The philosophy of the semantic web. Understand why using the right element for the right content is critical for SEO, accessibility, and site longevity.',
                        keywords: ['philosophy of semantics', 'why semantic html', 'benefit of clean code', 'meaningful markup', 'web architecture basics']
                    },
                    {
                        id: 'html-layout',
                        title: 'Semantic Layout (header, main, footer)',
                        slug: 'html-layout',
                        contentFile: 'HTML/HTMLLayout',
                        excerpt: 'Mastering modern page layout. Learn to use landmark roles like header, main, and footer to define the high-level structural areas of your website.',
                        keywords: ['html5 layout tags', 'header vs main vs footer', 'landmark roles a11y', 'page skeleton html', 'modern layout architecture']
                    },
                    {
                        id: 'html-sections',
                        title: 'Content Sections (section, article, aside)',
                        slug: 'html-sections',
                        contentFile: 'HTML/HTMLSections',
                        excerpt: 'Sectioning content professionaly. Understand the precise semantic differences between <section>, <article>, and <aside> for perfect content hierarchy.',
                        keywords: ['article vs section html', 'aside tag use case', 'semantic sectioning', 'content grouping tags', 'seo structure hierarchy']
                    },
                    {
                        id: 'html-semantics-mastery',
                        title: 'Complete Semantic Page Structure',
                        slug: 'html-semantics',
                        contentFile: 'HTML/HTMLSemanticMastery',
                        excerpt: 'The professional standard for web architecture. Learn how to synthesize all semantic tags into a robust, accessible, and SEO-perfect document tree.',
                        keywords: ['professional html architecture', 'complete semantic page', 'accessible content structure', 'clean code standards', 'semantic mastery']
                    }
                ]
            },
            {
                id: 'html-accessibility',
                title: '10. Accessibility (A11Y)',
                lessons: [
                    {
                        id: 'html-aria-basics',
                        title: 'ARIA Roles & Attributes',
                        slug: 'html-aria-basics',
                        contentFile: 'HTML/HTMLAriaBasics',
                        excerpt: 'Core ARIA roles and state management. Learn how Accessible Rich Internet Applications (ARIA) bridge the accessibility gap for dynamic web components.',
                        keywords: ['aria roles web', 'aria label patterns', 'accessible rich applications', 'aria states web developers', 'dynamic accessibility']
                    },
                    {
                        id: 'html-accessibility',
                        title: 'Accessible HTML Best Practices',
                        slug: 'html-accessibility',
                        contentFile: 'HTML/HTMLAccessibility',
                        excerpt: 'Essential accessibility for the modern web. Learn to implement keyboard navigation, color contrast, and proper labeling for a truly inclusive user experience.',
                        keywords: ['a11y best practices', 'wcag guidelines intro', 'inclusive web design', 'accessibility audit html', 'web engineering a11y']
                    },
                    {
                        id: 'html-screen-readers',
                        title: 'Optimizing for Screen Readers',
                        slug: 'html-screen-readers',
                        contentFile: 'HTML/HTMLScreenReaders',
                        excerpt: 'Optimizing web content for screen readers. Learn how browsers communicate with assistive technologies and how to write code that talks back effectively.',
                        keywords: ['screen reader optimization', 'nvda jaws voiceover', 'descriptive text a11y', 'assistive technology web', 'vocalizing html']
                    },
                    {
                        id: 'html-keyboard-nav',
                        title: 'Keyboard Navigation & Focus Management',
                        slug: 'html-keyboard-nav',
                        contentFile: 'HTML/HTMLKeyboardNav',
                        excerpt: 'Advanced keyboard focus management. Master the tab order, focus traps, and logical navigation flows for power users and keyboard-only learners.',
                        keywords: ['keyboard navigation web', 'focus management html', 'tabindex property', 'focus trap design', 'logical nav flows']
                    }
                ]
            },
            {
                id: 'html-seo',
                title: '11. SEO Optimization',
                lessons: [
                    {
                        id: 'html-seo-basics',
                        title: 'HTML for SEO: The Fundamentals',
                        slug: 'html-seo-basics',
                        contentFile: 'HTML/HTMLSeoBasics',
                        excerpt: 'Master the fundamentals of HTML for SEO. Learn how to optimize titles, headings, and descriptions to ensure your content is easily discovered by search engines.',
                        keywords: ['html seo basics', 'search engine optimization html', 'on-page seo tags', 'meta descriptions seo', 'seo for beginners']
                    },
                    {
                        id: 'html-structured-data',
                        title: 'Structured Data & Schema.org',
                        slug: 'html-structured-data',
                        contentFile: 'HTML/HTMLStructuredData',
                        excerpt: 'Implementing structured data with Schema.org. Learn how to provide explicit meaning to your content and achieve rich results in search engines.',
                        keywords: ['structured data html', 'schema.org microdata', 'json-ld injection', 'rich snippets seo', 'semantic data web']
                    },
                    {
                        id: 'html-open-graph',
                        title: 'Open Graph & Social Media Meta Tags',
                        slug: 'html-open-graph',
                        contentFile: 'HTML/HTMLOpenGraph',
                        excerpt: 'Optimizing for social sharing with Open Graph. Learn how to control how your content appears on Facebook, Twitter, and LinkedIn using specific meta tags.',
                        keywords: ['open graph protocol', 'og tags html', 'social media optimization', 'twitter cards tags', 'social sharing seo']
                    },
                    {
                        id: 'html-performance',
                        title: 'Performance Optimization for SEO',
                        slug: 'html-performance',
                        contentFile: 'HTML/HTMLPerformance',
                        excerpt: 'Web performance as an SEO ranking factor. Learn techniques to improve Core Web Vitals, including resource hints and efficient asset delivery.',
                        keywords: ['web performance seo', 'core web vitals html', 'resource hints dns-prefetch', 'page speed optimization', 'frontend performance budget']
                    }
                ]
            },
            {
                id: 'html-apis',
                title: '12. HTML5 APIs',
                lessons: [
                    {
                        id: 'html-geolocation',
                        title: 'Geolocation API',
                        slug: 'html-geolocation',
                        contentFile: 'HTML/HTMLGeolocation',
                        excerpt: 'User location awareness with the Geolocation API. Learn how to securely request and utilize geographic coordinates to build location-based web experiences.',
                        keywords: ['html5 geolocation api', 'user location web', 'geographic coordinates js', 'location permission web', 'navigaton api']
                    },
                    {
                        id: 'html-storage',
                        title: 'Web Storage Basics',
                        slug: 'html-storage',
                        contentFile: 'HTML/HTMLStorage',
                        excerpt: 'Client-side data persistence with Web Storage. Master localStorage and sessionStorage for managing user state and preferences without a database.',
                        keywords: ['html5 web storage', 'localstorage vs sessionstorage', 'client-side persistence', 'browser data storage', 'frontend state management']
                    },
                    {
                        id: 'html-drag-drop',
                        title: 'Drag and Drop API',
                        slug: 'html-drag-drop',
                        contentFile: 'HTML/HTMLDragDrop',
                        excerpt: 'Building interactive Drag and Drop interfaces. Learn how to use the native HTML5 Drag and Drop API to create intuitive, complex user interactions.',
                        keywords: ['html5 drag and drop', 'draggable attribute', 'droppable areas', 'web drag drop api', 'interactive ui patterns']
                    },
                    {
                        id: 'html-web-workers',
                        title: 'Web Workers Introduction',
                        slug: 'html-web-workers',
                        contentFile: 'HTML/HTMLWebWorkers',
                        excerpt: 'Offloading heavy computations with Web Workers. Learn how to run background scripts to keep your UI responsive during intensive data processing.',
                        keywords: ['web workers html5', 'background processing web', 'multithreading javascript', 'worker thread basics', 'preventing ui freeze']
                    }
                ]
            },
            {
                id: 'html-responsive',
                title: '13. Responsive HTML',
                lessons: [
                    {
                        id: 'html-viewport',
                        title: 'Viewport & Mobile Optimization',
                        slug: 'html-viewport',
                        contentFile: 'HTML/HTMLViewport',
                        excerpt: 'Responsive viewport control for mobile devices. Learn to use the viewport meta tag and CSS units to ensure your site renders perfectly on every screen.',
                        keywords: ['html viewport meta', 'mobile optimization basics', 'responsive viewport settings', 'touch-action property', 'mobile web engineering']
                    },
                    {
                        id: 'html-responsive-design',
                        title: 'Responsive Design Principles',
                        slug: 'html-responsive-design',
                        contentFile: 'HTML/HTMLResponsiveDesign',
                        excerpt: 'Foundational principles of responsive web design. Master fluid layouts, flexible grids, and a mobile-first philosophy for modern frontend engineering.',
                        keywords: ['responsive design principles', 'fluid layouts html', 'flexible media web', 'grid vs flexbox foundations', 'frontend architectural patterns']
                    },
                    {
                        id: 'html-mobile-first',
                        title: 'Mobile-First HTML Strategy',
                        slug: 'html-mobile-first',
                        contentFile: 'HTML/HTMLMobileFirst',
                        excerpt: 'The Mobile-First development strategy. Learn why starting with mobile constraints leads to cleaner code, better performance, and superior user experience.',
                        keywords: ['mobile-first development', 'progressive enhancement html', 'responsive strategy', 'mobile web performance', 'lean html architecture']
                    }
                ]
            },
            {
                id: 'html-best-practices',
                title: '14. Professional Best Practices',
                lessons: [
                    {
                        id: 'html-code-quality',
                        title: 'Writing Clean, Maintainable HTML',
                        slug: 'html-code-quality',
                        contentFile: 'HTML/HTMLCodeQuality',
                        excerpt: 'Professional HTML code quality and maintainability. Learn naming conventions, nesting best practices, and documentation strategies for large-scale projects.',
                        keywords: ['clean html code', 'maintainable markup', 'html naming conventions', 'consistent code style', 'professional frontend habits']
                    },
                    {
                        id: 'html-validation',
                        title: 'HTML Validation & Standards',
                        slug: 'html-validation',
                        contentFile: 'HTML/HTMLValidation',
                        excerpt: 'Ensuring technical compliance with HTML standards. Learn to use validators to identify errors, improve cross-browser compatibility, and guarantee markup integrity.',
                        keywords: ['w3c validation html', 'checking markup errors', 'web standards compliance', 'validator services', 'cross-browser reliability']
                    },
                    {
                        id: 'html-performance-opt',
                        title: 'Performance Optimization Techniques',
                        slug: 'html-performance-opt',
                        contentFile: 'HTML/HTMLPerformanceOpt',
                        excerpt: 'Advanced techniques for high-performance HTML. Learn about critical rendering paths, resource prioritization, and reducing DOM size for fast loading.',
                        keywords: ['html performance optimization', 'critical path rendering', 'prefetch prerender', 'optimizing dom size', 'frontend speed hacks']
                    },
                    {
                        id: 'html-security',
                        title: 'HTML Security Best Practices',
                        slug: 'html-security',
                        contentFile: 'HTML/HTMLSecurity',
                        excerpt: 'Security-first HTML development. Learn to prevent XSS attacks, use CSP headers, and implement secure attributes to protect your users and data.',
                        keywords: ['html security best practices', 'preventing xss html', 'csp headers intro', 'secure link attributes', 'webapp security foundations']
                    }
                ]
            },
            {
                id: 'html-modern',
                title: '15. Modern HTML & Future',
                lessons: [
                    {
                        id: 'html-components',
                        title: 'Web Components & Custom Elements',
                        slug: 'html-components',
                        contentFile: 'HTML/HTMLComponents',
                        excerpt: 'Modular architecture with Web Components. Learn to create custom, reusable HTML elements with Shadow DOM and templates for true encapsulated engineering.',
                        keywords: ['web components html5', 'custom elements shadow dom', 'encapsulated html', 'reusable web modules', 'frontend component architecture']
                    },
                    {
                        id: 'html-pwa',
                        title: 'PWA Basics',
                        slug: 'html-pwa',
                        contentFile: 'HTML/HTMLPwa',
                        excerpt: 'Fundamentals of Progressive Web Apps. Learn about manifest files and service worker integration to make your HTML site feel like a native application.',
                        keywords: ['pwa basics html', 'web app manifest', 'offline web apps', 'service worker intro', 'native-like web experiences']
                    },
                    {
                        id: 'html-future',
                        title: 'The Future of HTML',
                        slug: 'html-future',
                        contentFile: 'HTML/HTMLFuture',
                        excerpt: 'Exploring the future of web standards. Look ahead at upcoming HTML features, proposed elements, and the evolving landscape of total web experience.',
                        keywords: ['future of html', 'upcoming web features', 'html6 speculation', 'modern web evolution', 'what is next for html']
                    }
                ]
            }
        ]
    },
    {
        id: 'js-modern-course',
        title: 'JavaScript Mastery: From Fundamentals to Modern ES2024+',
        description: 'Comprensive JavaScript education for modern engineering. Master the core language, advanced asynchronous patterns, DOM architecture, and the latest ES2024+ features using production-grade best practices.',
        language: 'javascript',
        chapters: [
            {
                id: 'js-fundamentals',
                title: '1. JavaScript Fundamentals',
                lessons: [
                    {
                        id: 'js-introduction',
                        title: 'JavaScript Introduction & History',
                        slug: 'javascript-introduction',
                        contentFile: 'JS/JSIntroduction',
                        excerpt: 'Discover the evolution of JavaScript from a simple scripting tool to the engine of the modern web. Learn high-level history and the V8 ecosystem.',
                        keywords: ['javascript history', 'what is javascript', 'v8 engine intro', 'web development evolution', 'is javascript worth learning']
                    },
                    {
                        id: 'js-variables',
                        title: 'Variables & Scope (let, const, var)',
                        slug: 'javascript-variables-scope',
                        contentFile: 'JS/JSVariables',
                        excerpt: 'Master JavaScript variable declaration and scoping. Understand block scope vs function scope and why const is the safest default for engineers.',
                        keywords: ['javascript variables', 'let vs const vs var', 'block scope', 'temporal dead zone', 'variable hoisting explained']
                    },
                    {
                        id: 'js-data-types',
                        title: 'Data Types & Type System',
                        slug: 'javascript-data-types',
                        contentFile: 'JS/JSDataTypes',
                        excerpt: 'Deep dive into the JavaScript type system. Explore primitives, reference types, and the technical reasons behind the dynamic nature of JS types.',
                        keywords: ['javascript data types', 'primitives vs objects', 'typeof operator', 'dynamic typing', 'javascript type system']
                    },
                    {
                        id: 'js-type-coercion',
                        title: 'Type Coercion & Conversion',
                        slug: 'javascript-type-coercion',
                        contentFile: 'JS/JSTypeCoercion',
                        excerpt: 'Understand how JavaScript implicitly converts types. Master explicit conversion and avoid the common pitfalls of == vs === in production code.',
                        keywords: ['type coercion', 'explicit type conversion', 'strict equality', 'javascript bugs coercion', 'falsy values js']
                    },
                    {
                        id: 'js-operators',
                        title: 'Operators & Expressions',
                        slug: 'javascript-operators',
                        contentFile: 'JS/JSOperators',
                        excerpt: 'A comprehensive guide to JavaScript operators. From arithmetic and assignment to bitwise and logical shortcuts for cleaner code.',
                        keywords: ['javascript operators', 'logical operators', 'bitwise operations js', 'operator precedence', 'assignment operators']
                    }
                ]
            },
            {
                id: 'js-functions',
                title: '2. Functions Deep Dive',
                lessons: [
                    {
                        id: 'js-function-basics',
                        title: 'Function Fundamentals',
                        slug: 'javascript-functions',
                        contentFile: 'JS/JSFunctions',
                        excerpt: 'Master the core building blocks of JavaScript: Functions. Learn declarations, expressions, and parameters for reusable engineering.',
                        keywords: ['javascript functions', 'function declaration', 'function expression', 'parameters vs arguments', 'pure functions']
                    },
                    {
                        id: 'js-arrow-functions',
                        title: 'Arrow Functions & This Binding',
                        slug: 'javascript-arrow-functions',
                        contentFile: 'JS/JSArrowFunctions',
                        excerpt: 'Understand the syntax and scoping benefits of arrow functions. Master lexical scoping and how arrow functions handle the this keyword.',
                        keywords: ['arrow functions', 'this keyword', 'lexical scope', 'javascript anonymous functions', 'es6 function syntax']
                    },
                    {
                        id: 'js-iife',
                        title: 'IIFE & Function Scope',
                        slug: 'javascript-iife',
                        contentFile: 'JS/JSIIFE',
                        excerpt: 'Learn the design pattern of Immediately Invoked Function Expressions. Protect global scope and implement module-like encapsulation.',
                        keywords: ['iife javascript', 'self-invoking functions', 'encapsulation js', 'privacy patterns', 'global scope pollution']
                    },
                    {
                        id: 'js-closures',
                        title: 'Closures & Lexical Scope',
                        slug: 'javascript-closures',
                        contentFile: 'JS/JSClosures',
                        excerpt: 'One of the most powerful features of JS: Closures. Understand how functions remember their creation scope for state management.',
                        keywords: ['javascript closures', 'lexical environment', 'private variables js', 'functional programming js', 'scope chain']
                    },
                    {
                        id: 'js-higher-order',
                        title: 'Higher-Order Functions',
                        slug: 'javascript-higher-order-functions',
                        contentFile: 'JS/JSHigherOrder',
                        excerpt: 'Elevate your code with higher-order functions. Learn to treat functions as first-class citizens: passing them as arguments and returning them.',
                        keywords: ['higher order functions', 'first class functions', 'callback functions', 'functional patterns', 'abstraction in js']
                    },
                    {
                        id: 'js-recursion',
                        title: 'Recursion & Stack',
                        slug: 'javascript-recursion',
                        contentFile: 'JS/JSRecursion',
                        excerpt: 'Master recursion in JavaScript. Learn to solve complex nested problems and understand call stack limits to avoid stack overflows.',
                        keywords: ['javascript recursion', 'recursive functions', 'call stack', 'base case recursion', 'nested data processing']
                    }
                ]
            },
            {
                id: 'js-objects-arrays',
                title: '3. Objects & Arrays',
                lessons: [
                    {
                        id: 'js-objects',
                        title: 'Objects & Properties',
                        slug: 'javascript-objects',
                        contentFile: 'JS/JSObjects',
                        excerpt: 'Master the core data structure of JavaScript. Learn object literals, property access, and how objects work under the hood in the JS environment.',
                        keywords: ['javascript objects', 'object literals', 'computed properties', 'property descriptors', 'nested objects']
                    },
                    {
                        id: 'js-arrays',
                        title: 'Arrays & Array Methods',
                        slug: 'javascript-arrays',
                        contentFile: 'JS/JSArrays',
                        excerpt: 'Power up your data processing with modern array methods. Master map, filter, reduce, and the latest ES2023 additions for immutable operations.',
                        keywords: ['javascript arrays', 'higher order array methods', 'map filter reduce', 'array performance', 'immutable arrays']
                    },
                    {
                        id: 'js-destructuring',
                        title: 'Destructuring Assignment',
                        slug: 'javascript-destructuring',
                        contentFile: 'JS/JSDestructuring',
                        excerpt: 'Simplify your code with destructuring. Learn to extract data from objects and arrays with clean, expressive syntax for better readability.',
                        keywords: ['js destructuring', 'object destructuring', 'array destructuring', 'clean code patterns', 'extracting data js']
                    },
                    {
                        id: 'js-spread-rest',
                        title: 'Spread & Rest Operators',
                        slug: 'javascript-spread-rest',
                        contentFile: 'JS/JSSpreadRest',
                        excerpt: 'Understand the three dots (...). Learn the difference between spreading data and collecting arguments using the powerful rest parameter.',
                        keywords: ['spread operator', 'rest parameter', 'shallow cloning', 'merging objects', 'flexible functions js']
                    },
                    {
                        id: 'js-object-methods',
                        title: 'Object Methods & This',
                        slug: 'javascript-object-methods',
                        contentFile: 'JS/JSObjectMethods',
                        excerpt: 'Advanced object manipulation. Master this context in object methods and understand how to bind, call, and apply for precise execution control.',
                        keywords: ['javascript object methods', 'this keyword context', 'bind call apply', 'method shorthand', 'advanced objects']
                    }
                ]
            },
            {
                id: 'js-prototypes-classes',
                title: '4. Prototypes & Classes',
                lessons: [
                    {
                        id: 'js-prototypes',
                        title: 'Prototypal Inheritance',
                        slug: 'javascript-prototypes',
                        contentFile: 'JS/JSPrototypes',
                        excerpt: 'Deep dive into the prototype chain. Understand how JavaScript objects link to one another and why prototypes are the foundation of JS OOP.',
                        keywords: ['prototypal inheritance', 'prototype chain', 'js __proto__', 'constructor functions', 'inheritance in js']
                    },
                    {
                        id: 'js-inheritance',
                        title: 'Inheritance Patterns',
                        slug: 'javascript-inheritance',
                        contentFile: 'JS/JSInheritance',
                        excerpt: 'Explore various inheritance patterns from functional and constructor-based styles to the modern class-based syntactic sugar.',
                        keywords: ['inheritance patterns js', 'object.create', 'functional inheritance', 'constructor inheritance', 'extending objects']
                    },
                    {
                        id: 'js-classes',
                        title: 'ES6 Classes',
                        slug: 'javascript-classes',
                        contentFile: 'JS/JSClasses',
                        excerpt: 'Master modern class syntax. Learn constructors, super(), and instance vs static properties for clean, structured application code.',
                        keywords: ['es6 classes', 'javascript class syntax', 'constructors', 'super keyword', 'modern js architecture']
                    },
                    {
                        id: 'js-private-fields',
                        title: 'Private Fields & Methods',
                        slug: 'javascript-private-fields',
                        contentFile: 'JS/JSPrivateFields',
                        excerpt: 'Protect your object state with true private fields using the # syntax. Learn modern encapsulation for secure and maintainable software.',
                        keywords: ['private fields js', 'encapsulation classes', 'hash syntax js', 'secure data patterns', 'robust class design']
                    }
                ]
            },
            {
                id: 'js-dom',
                title: '5. DOM Manipulation',
                lessons: [
                    {
                        id: 'js-dom-intro',
                        title: 'DOM Introduction & Tree Structure',
                        slug: 'javascript-dom-intro',
                        contentFile: 'JS/JSDOMIntro',
                        excerpt: 'Learn the architecture of the Document Object Model. Understand how browsers interpret HTML into a programmable tree structure.',
                        keywords: ['what is dom', 'dom tree', 'browser rendering', 'document object basics', 'manipulating web pages']
                    },
                    {
                        id: 'js-dom-selection',
                        title: 'Selecting Elements',
                        slug: 'javascript-dom-selection',
                        contentFile: 'JS/JSDOMSelection',
                        excerpt: 'Master element selection with querySelector, getElementById, and more. Learn the performance trade-offs of different selection methods.',
                        keywords: ['dom selection', 'queryselector vs getelementbyid', 'htmlcollection vs nodelist', 'efficient dom selection', 'modern js dom']
                    },
                    {
                        id: 'js-dom-manipulation',
                        title: 'Modifying DOM Elements',
                        slug: 'javascript-dom-manipulation',
                        contentFile: 'JS/JSDOMManipulation',
                        excerpt: 'Learn to safely modify text, HTML, and attributes. Master classList and style properties for dynamic UI updates.',
                        keywords: ['dom manipulation', 'textcontent vs innerhtml', 'classlist api', 'attribute manipulation', 'dynamic ui updates']
                    },
                    {
                        id: 'js-dom-creation',
                        title: 'Creating & Inserting Elements',
                        slug: 'javascript-dom-creation',
                        contentFile: 'JS/JSDOMCreation',
                        excerpt: 'Build dynamic interfaces by creating elements from scratch. Learn appendChild, insertBefore, and modern insertion methods.',
                        keywords: ['creating dom elements', 'document.createelement', 'appending elements', 'dynamic content loading', 'dom insertion methods']
                    },
                    {
                        id: 'js-dom-traversal',
                        title: 'DOM Traversal & Performance',
                        slug: 'javascript-dom-traversal',
                        contentFile: 'JS/JSDOMTraversal',
                        excerpt: 'Navigate the DOM tree efficiently. Learn to find parents, children, and siblings while avoiding expensive re-traversals.',
                        keywords: ['dom traversal', 'parentnode', 'children vs childnodes', 'finding elements js', 'dom performance']
                    }
                ]
            },
            {
                id: 'js-events',
                title: '6. Events & Event Handling',
                lessons: [
                    {
                        id: 'js-events-basics',
                        title: 'Event Handling Basics',
                        slug: 'javascript-events',
                        contentFile: 'JS/JSEvents',
                        excerpt: 'Connect your code to user interactions. Learn event listeners, event objects, and how to handle keyboard, mouse, and form events correctly.',
                        keywords: ['event listeners', 'event object', 'handling user input', 'browser events', 'interactive web pages']
                    },
                    {
                        id: 'js-event-delegation',
                        title: 'Event Delegation & Bubbling',
                        slug: 'javascript-event-delegation',
                        contentFile: 'JS/JSEventDelegation',
                        excerpt: 'Master the event lifecycle. Understand capturing, bubbling, and how event delegation can significantly improve your application\'s performance.',
                        keywords: ['event bubbling', 'event capturing', 'event delegation', 'stoppropagation', 'performant event handling']
                    },
                    {
                        id: 'js-custom-events',
                        title: 'Custom Events',
                        slug: 'javascript-custom-events',
                        contentFile: 'JS/JSCustomEvents',
                        excerpt: 'Go beyond the browser defaults. Learn to create and dispatch custom events to build sophisticated, decoupled communication in your UI.',
                        keywords: ['custom events js', 'customevent api', 'dispatchevent', 'decoupled architecture', 'inter-component communication']
                    },
                    {
                        id: 'js-event-loop',
                        title: 'Event Loop & Call Stack',
                        slug: 'javascript-event-loop',
                        contentFile: 'JS/JSEventLoop',
                        excerpt: 'The engine of asynchronous JS. Master the call stack, task queue, microtask queue, and the event loop to write non-blocking code.',
                        keywords: ['javascript event loop', 'call stack', 'microtask queue', 'task queue', 'blocking vs non-blocking']
                    }
                ]
            },
            {
                id: 'js-async',
                title: '7. Asynchronous JavaScript',
                lessons: [
                    {
                        id: 'js-callbacks',
                        title: 'Callbacks & Callback Hell',
                        slug: 'javascript-callbacks',
                        contentFile: 'JS/JSCallbacks',
                        excerpt: 'Learn the foundational pattern of asynchronous JS. Understand callback functions and how to manage nested logic without creating callback hell.',
                        keywords: ['callbacks js', 'callback hell', 'asynchronous patterns', 'higher order functions', 'nested logic handling']
                    },
                    {
                        id: 'js-promises',
                        title: 'Promises & Promise Chaining',
                        slug: 'javascript-promises',
                        contentFile: 'JS/JSPromises',
                        excerpt: 'Master the Promise API. Learn to handle eventual values, handle errors with .catch(), and link asynchronous operations via chaining.',
                        keywords: ['javascript promises', 'promise state', 'promise chaining', 'error handling promises', 'asynchronous orchestration']
                    },
                    {
                        id: 'js-async-await',
                        title: 'Async/Await Mastery',
                        slug: 'javascript-async-await',
                        contentFile: 'JS/JSAsyncAwait',
                        excerpt: 'The modern way to handle async code. Learn to write asynchronous logic that reads like synchronous code using the powerful async and await keywords.',
                        keywords: ['async await', 'modern async js', 'yielding execution', 'sequential async', 'clean asynchronous code']
                    },
                    {
                        id: 'js-fetch',
                        title: 'Fetch API & HTTP Requests',
                        slug: 'javascript-fetch-api',
                        contentFile: 'JS/JSFetch',
                        excerpt: 'Communicate with external servers. Master the Fetch API, from basic GET requests to advanced POST operations with headers and bodies.',
                        keywords: ['fetch api', 'http requests js', 'ajax modern', 'rest api consumption', 'promises fetch']
                    },
                    {
                        id: 'js-error-async',
                        title: 'Error Handling in Async Code',
                        slug: 'javascript-async-errors',
                        contentFile: 'JS/JSAsyncErrors',
                        excerpt: 'Build robust applications. Master try/catch with async/await and learn patterns to handle network failures and API errors gracefully.',
                        keywords: ['async error handling', 'try catch async', 'handling failed fetches', 'robust networking js', 'unhandled rejections']
                    },
                    {
                        id: 'js-parallel',
                        title: 'Parallel & Sequential Execution',
                        slug: 'javascript-parallel-execution',
                        contentFile: 'JS/JSParallel',
                        excerpt: 'Optimize your async performance. Learn to use Promise.all to execute operations in parallel and understand when to keep them sequential.',
                        keywords: ['promise.all', 'parallel async', 'sequential execution', 'optimizing performance js', 'asynchronous concurrency']
                    }
                ]
            },
            {
                id: 'js-es6-features',
                title: '8. ES6+ Modern Features',
                lessons: [
                    {
                        id: 'js-template-literals',
                        title: 'Template Literals & Tagged Templates',
                        slug: 'javascript-template-literals',
                        contentFile: 'JS/JSTemplateLiterals',
                        excerpt: 'Go beyond basic strings. Master backticks, expression interpolation, and multi-line strings. Discover tagged templates for advanced DSLs in JavaScript.',
                        keywords: ['template literals', 'tagged templates', 'interpolation js', 'multi-line strings', 'es6 string features']
                    },
                    {
                        id: 'js-optional-chaining',
                        title: 'Optional Chaining and Nullish',
                        slug: 'javascript-optional-chaining',
                        contentFile: 'JS/JSOptionalChaining',
                        excerpt: 'Safely access deep object properties. Learn to use ?. and ?? to handle null or undefined values gracefully without verbose safety checks.',
                        keywords: ['optional chaining', 'nullish coalescing', 'safe navigation', 'javascript defaults', 'handling undefined']
                    },
                    {
                        id: 'js-symbols',
                        title: 'Symbols & Well-Known Symbols',
                        slug: 'javascript-symbols',
                        contentFile: 'JS/JSSymbols',
                        excerpt: 'Understand the unique Symbol primitive. Learn to create private-like properties and use well-known symbols to customize built-in JS behavior.',
                        keywords: ['javascript symbols', 'well-known symbols', 'iterator protocol', 'unique identifiers js', 'primitive types']
                    },
                    {
                        id: 'js-iterators',
                        title: 'Iterators & Iterables',
                        slug: 'javascript-iterators',
                        contentFile: 'JS/JSIterators',
                        excerpt: 'Master the iteration protocol. Learn how for...of loops work under the hood and how to make your own custom objects iterable.',
                        keywords: ['iterator protocol', 'iterables js', 'next method', 'symbol.iterator', 'custom iteration']
                    },
                    {
                        id: 'js-generators',
                        title: 'Generators & Yield',
                        slug: 'javascript-generators',
                        contentFile: 'JS/JSGenerators',
                        excerpt: 'Write pauseable functions. Master function* and yield to handle complex sequences, custom iterators, and asynchronous control flows.',
                        keywords: ['javascript generators', 'yield keyword', 'pauseable functions', 'iterator factory', 'stateful functions']
                    }
                ]
            },
            {
                id: 'js-modules',
                title: '9. Modules & Code Organization',
                lessons: [
                    {
                        id: 'js-modules-intro',
                        title: 'ES6 Modules Introduction',
                        slug: 'javascript-modules',
                        contentFile: 'JS/JSModules',
                        excerpt: 'Modernize your project structure. Learn the history of modules and how ES6 import/export changed the way we build scalable JS applications.',
                        keywords: ['es6 modules', 'js modularity', 'script type module', 'modular programming', 'javascript history']
                    },
                    {
                        id: 'js-import-export',
                        title: 'Import & Export Patterns',
                        slug: 'javascript-import-export',
                        contentFile: 'JS/JSImportExport',
                        excerpt: 'Master naming and default exports. Learn various patterns for sharing code between files while maintaining clean and predictable interfaces.',
                        keywords: ['default export', 'named export', 'import alias', 'namespace import', 're-exporting']
                    },
                    {
                        id: 'js-dynamic-imports',
                        title: 'Dynamic Imports & Code Splitting',
                        slug: 'javascript-dynamic-imports',
                        contentFile: 'JS/JSDynamicImports',
                        excerpt: 'Optimize your bundle size. Master import() for lazy loading components and features only when the user needs them.',
                        keywords: ['dynamic import', 'code splitting', 'lazy loading js', 'bundle optimization', 'performant web apps']
                    }
                ]
            },
            {
                id: 'js-error-handling',
                title: '10. Error Handling & Debugging',
                lessons: [
                    {
                        id: 'js-try-catch',
                        title: 'Try/Catch & Error Types',
                        slug: 'javascript-try-catch',
                        contentFile: 'JS/JSTryCatch',
                        excerpt: 'Handle runtime failures professionally. Master the try-catch-finally block and learn the different built-in error types in JavaScript.',
                        keywords: ['try catch', 'finally block', 'error types js', 'runtime errors', 'exception handling']
                    },
                    {
                        id: 'js-custom-errors',
                        title: 'Custom Error Handling Patterns',
                        slug: 'javascript-custom-errors',
                        contentFile: 'JS/JSCustomErrors',
                        excerpt: 'Create your own error hierarchies. Learn to extend the Error class to build domain-specific error handling for large scale applications.',
                        keywords: ['custom errors', 'extending error class', 'error handling patterns', 'software robustness', 'debuggable code']
                    },
                    {
                        id: 'js-debugging',
                        title: 'Debugging Techniques & DevTools',
                        slug: 'javascript-debugging',
                        contentFile: 'JS/JSDebugging',
                        excerpt: 'Master the Chrome DevTools. Learn breakpoints, watch expressions, and advanced console methods to find and fix bugs faster.',
                        keywords: ['debugging js', 'chrome devtools', 'breakpoints', 'console methods', 'troubleshooting javascript']
                    }
                ]
            },
            {
                id: 'js-regex',
                title: '11. Regular Expressions',
                lessons: [
                    {
                        id: 'js-regex-basics',
                        title: 'Regex Fundamentals',
                        slug: 'javascript-regex-basics',
                        contentFile: 'JS/JSRegexBasics',
                        excerpt: 'Master the language of patterns. Learn regex literals, constructors, and the basic meta-characters for powerful text processing in JS.',
                        keywords: ['regex basics', 'regular expressions js', 'pattern matching', 'test method', 'string search']
                    },
                    {
                        id: 'js-regex-patterns',
                        title: 'Regex Patterns & Flags',
                        slug: 'javascript-regex-patterns',
                        contentFile: 'JS/JSRegexPatterns',
                        excerpt: 'Deep dive into regex syntax. Learn groups, quantifiers, anchors, and flags like g, i, and m for sophisticated string validation.',
                        keywords: ['regex patterns', 'regex flags', 'capturing groups', 'quantifiers', 'string validation']
                    },
                    {
                        id: 'js-regex-practical',
                        title: 'Practical Regex Use Cases',
                        slug: 'javascript-regex-practical',
                        contentFile: 'JS/JSRegexPractical',
                        excerpt: 'Apply your regex knowledge. Learn to solve real-world problems like email validation, URL parsing, and advanced text replacement.',
                        keywords: ['practical regex', 'email validation regex', 'url parsing', 'searching strings', 'advanced text cleaning']
                    }
                ]
            },
            {
                id: 'js-browser-apis',
                title: '12. Browser APIs',
                lessons: [
                    {
                        id: 'js-local-storage',
                        title: 'Local Storage & Session Storage',
                        slug: 'javascript-local-storage',
                        contentFile: 'JS/JSLocalStorage',
                        excerpt: 'Persist data in the browser. Learn the differences between LocalStorage and SessionStorage, their quota limits, and how to store complex objects safely.',
                        keywords: ['localstorage', 'sessionstorage', 'web storage api', 'persistent data js', 'client-side storage']
                    },
                    {
                        id: 'js-fetch-advanced',
                        title: 'Advanced Fetch & AJAX',
                        slug: 'javascript-fetch-advanced',
                        contentFile: 'JS/JSFetchAdvanced',
                        excerpt: 'Master advanced networking. Learn AbortController for canceling requests, handling upload progress, and building robust interceptors for API calls.',
                        keywords: ['advanced fetch', 'abortcontroller', 'api interceptors', 'request timeout', 'networking patterns js']
                    },
                    {
                        id: 'js-file-api',
                        title: 'File API & File Handling',
                        slug: 'javascript-file-api',
                        contentFile: 'JS/JSFileAPI',
                        excerpt: 'Process local files in the browser. Learn to use the File and Blob APIs to read, slice, and manipulate user-uploaded data for heavy-duty applications.',
                        keywords: ['javascript file api', 'filereader', 'blob manipulation', 'client-side file processing', 'uploading files js']
                    },
                    {
                        id: 'js-intersection-observer',
                        title: 'Intersection Observer API',
                        slug: 'javascript-intersection-observer',
                        contentFile: 'JS/JSIntersectionObserver',
                        excerpt: 'Build high-performance UIs. Master the Intersection Observer for lazy loading images, infinite scrolling, and trigger-based animations without scroll-jank.',
                        keywords: ['intersection observer', 'lazy loading', 'infinite scroll', 'viewport visibility', 'performant animations']
                    },
                    {
                        id: 'js-history-api',
                        title: 'History API & Navigation',
                        slug: 'javascript-history-api',
                        contentFile: 'JS/JSHistoryAPI',
                        excerpt: 'Master SPA navigation. Learn pushState, replaceState, and the popstate event to build seamless, router-like navigation without page refreshes.',
                        keywords: ['history api', 'pushstate', 'spa navigation', 'client side routing', 'back button handling js']
                    }
                ]
            },
            {
                id: 'js-performance',
                title: '13. Performance Optimization',
                lessons: [
                    {
                        id: 'js-memory',
                        title: 'Memory and Garbage Collection',
                        slug: 'javascript-memory-management',
                        contentFile: 'JS/JSMemory',
                        excerpt: 'Write memory-efficient code. Understand the Stack vs Heap, garbage collection strategies, and how to identify and fix memory leaks in production.',
                        keywords: ['javascript memory management', 'garbage collection', 'memory leaks', 'stack vs heap js', 'performance optimization']
                    },
                    {
                        id: 'js-debounce-throttle',
                        title: 'Debounce & Throttle',
                        slug: 'javascript-debounce-throttle',
                        contentFile: 'JS/JSDebounceThrottle',
                        excerpt: 'Optimize high-frequency events. Learn to limit event execution with debounce and throttle to improve scroll, resize, and search performance.',
                        keywords: ['debounce js', 'throttle js', 'event rate limiting', 'ui performance', 'search optimization']
                    },
                    {
                        id: 'js-code-splitting',
                        title: 'Code Splitting & Lazy Loading',
                        slug: 'javascript-code-splitting',
                        contentFile: 'JS/JSCodeSplitting',
                        excerpt: 'Reduce initial load times. Master advanced code splitting techniques and lazy loading patterns to deliver minimal JavaScript to your users.',
                        keywords: ['code splitting', 'lazy loading', 'performance optimization', 'webpack chunks', 'faster web apps']
                    },
                    {
                        id: 'js-profiling',
                        title: 'Performance Profiling & Optimization',
                        slug: 'javascript-profiling',
                        contentFile: 'JS/JSProfiling',
                        excerpt: 'Measure and improve your code. Master the Performance tab and profiling tools to eliminate bottlenecks and achieve 60FPS in the browser.',
                        keywords: ['js profiling', 'performance optimization', 'fps optimization', 'browser rendering pipeline', 'bottleneck identification']
                    }
                ]
            },
            {
                id: 'js-modern',
                title: '14. Modern JavaScript (ES2023+)',
                lessons: [
                    {
                        id: 'js-es2023',
                        title: 'ES2023+ Features',
                        slug: 'javascript-es2023-features',
                        contentFile: 'JS/JSES2023',
                        excerpt: 'Stay on the cutting edge. Explore the newest features of JavaScript, from immutable array methods (toSorted, toReversed) to advanced language syntax.',
                        keywords: ['es2023 features', 'modern javascript', 'immutable arrays', 'new js syntax', 'javascript 2024']
                    },
                    {
                        id: 'js-top-level-await',
                        title: 'Top-Level Await',
                        slug: 'javascript-top-level-await',
                        contentFile: 'JS/JSTopLevelAwait',
                        excerpt: 'Simplify your asynchronous modules. Learn to use await outside of async functions for cleaner module initialization and resource loading.',
                        keywords: ['top level await', 'es modules async', 'asynchronous initialization', 'modern js patterns', 'awaiting modules']
                    },
                    {
                        id: 'js-private-methods',
                        title: 'Private Methods & Accessors',
                        slug: 'javascript-private-methods',
                        contentFile: 'JS/JSPrivateMethods',
                        excerpt: 'Master true encapsulation in classes. Learn to use #private methods and accessors to build robust, secure, and maintainable object-oriented code.',
                        keywords: ['private methods js', 'class encapsulation', 'private getters setters', 'secure objects', 'class architecture']
                    },
                    {
                        id: 'js-temporal',
                        title: 'Temporal API (Future)',
                        slug: 'javascript-temporal-api',
                        contentFile: 'JS/JSTemporal',
                        excerpt: 'The future of Date handling. Discover the new Temporal API that solves historical Date bugs and provides powerful tools for timezone-safe arithmetic.',
                        keywords: ['temporal api', 'javascript dates', 'date-time handling', 'timezone math js', 'future of javascript']
                    }
                ]
            },
            {
                id: 'js-best-practices',
                title: '15. Best Practices & Patterns',
                lessons: [
                    {
                        id: 'js-design-patterns',
                        title: 'JavaScript Design Patterns',
                        slug: 'javascript-design-patterns',
                        contentFile: 'JS/JSDesignPatterns',
                        excerpt: 'Architect better applications. Master common design patterns like Singleton, Observer, Factory, and Module for scalable software development.',
                        keywords: ['js design patterns', 'singleton pattern', 'observer pattern', 'factory pattern', 'software architecture js']
                    },
                    {
                        id: 'js-code-quality',
                        title: 'Code Quality & Clean Code',
                        slug: 'javascript-code-quality',
                        contentFile: 'JS/JSCodeQuality',
                        excerpt: 'Write code that lasts. Learn industry-standard principles like SOLID, DRY, and KISS to maintain high code quality and reduce technical debt.',
                        keywords: ['code quality js', 'clean code principles', 'solid principles', 'dry kiss patterns', 'professional programming']
                    },
                    {
                        id: 'js-testing',
                        title: 'Testing JavaScript Code',
                        slug: 'javascript-testing',
                        contentFile: 'JS/JSTesting',
                        excerpt: 'Ensure your code works reliably. Learn the fundamentals of Unit, Integration, and E2E testing using modern tools like Vitest and Playwright.',
                        keywords: ['js testing', 'unit testing js', 'e2e testing', 'vitest tutorial', 'test driven development']
                    },
                    {
                        id: 'js-future',
                        title: 'The Future of JavaScript',
                        slug: 'javascript-future',
                        contentFile: 'JS/JSFuture',
                        excerpt: 'What\'s next for the web? Explore upcoming proposals and the future roadmap of JavaScript to stay ahead in your engineering career.',
                        keywords: ['future of js', 'tc39 proposals', 'web development trends', 'javascript roadmap 2026', 'next gen web']
                    }
                ]
            }
        ]
    },
    {
        id: 'c-fundamentals-course',
        title: 'C Programming: Low-Level Mastery',
        description: 'Master the foundations of computer science and high-performance engineering. Dive into memory management, pointers, and systems architecture with our comprehensive C curriculum.',
        language: 'c',
        chapters: [
            {
                id: 'c-introduction',
                title: '1. Introduction to C',
                lessons: [
                    {
                        id: 'c-overview',
                        title: 'Introduction to C Programming',
                        slug: 'c-programming-intro',
                        contentFile: 'C/COverview',
                        excerpt: 'Discover why C remains one of the most influential programming languages after 50 years. Learn its history, philosophy, and industrial importance.',
                        keywords: ['c programming history', 'why learn c', 'c language overview', 'systems programming intro', 'dennis ritchie c']
                    },
                    {
                        id: 'c-applications',
                        title: 'Why C? Use Cases & Applications',
                        slug: 'c-applications',
                        contentFile: 'C/CApplications',
                        excerpt: 'Explore the modern applications of C, from operating system kernels and embedded systems to high-performance game engines and databases.',
                        keywords: ['c language applications', 'where is c used', 'systems programming use cases', 'embedded systems c', 'game engine development']
                    },
                    {
                        id: 'c-standards',
                        title: 'C Standards (C99, C11, C18)',
                        slug: 'c-standards',
                        contentFile: 'C/CStandards',
                        excerpt: 'Understand the evolution of C through its standards: K&R, C89/C90, C99, C11, and C18. Learn why standards matter for portable engineering.',
                        keywords: ['c language standards', 'c99 vs c11', 'c18 standard', 'portable c code', 'ansi c history']
                    }
                ]
            },
            {
                id: 'c-setup',
                title: '2. Environment Setup',
                lessons: [
                    {
                        id: 'c-compiler-setup',
                        title: 'Compiler Setup (GCC/Clang)',
                        slug: 'c-compiler-setup',
                        contentFile: 'C/CCompilerSetup',
                        excerpt: 'Get your development environment ready. Learn to install and configure GCC and Clang on Windows, macOS, and Linux for professional C development.',
                        keywords: ['install c compiler', 'gcc setup guide', 'clang compiler config', 'c development environment', 'mingw windows setup']
                    },
                    {
                        id: 'c-first-program',
                        title: 'Your First C Program',
                        slug: 'c-first-program',
                        contentFile: 'C/CFirstProgram',
                        excerpt: 'Break the ice by writing and running your first C program. Master the "Hello World" structure and understand the purpose of every line.',
                        keywords: ['first c program', 'hello world c', 'c program structure', 'writing c code', 'c programming for beginners']
                    },
                    {
                        id: 'c-compilation-process',
                        title: 'Compilation Process',
                        slug: 'c-compilation-process',
                        contentFile: 'C/CCompilationProcess',
                        excerpt: 'Deep dive into what happens when you click "compile". Master the four stages: Preprocessing, Compilation, Assembly, and Linking.',
                        keywords: ['c compilation process', 'how c compiler works', 'preprocessing vs linking', 'gcc compilation stages', 'object files explained']
                    }
                ]
            },
            {
                id: 'c-basics',
                title: '3. C Fundamentals',
                lessons: [
                    {
                        id: 'c-syntax',
                        title: 'Basic Syntax & Structure',
                        slug: 'c-syntax',
                        contentFile: 'C/CSyntax',
                        excerpt: 'Master the fundamental rules of C syntax. Learn about tokens, identifiers, keywords, and the basic structural patterns required for valid code.',
                        keywords: ['c language syntax', 'c tokens and keywords', 'c coding rules', 'programming structure c', 'basic c grammar']
                    },
                    {
                        id: 'c-variables',
                        title: 'Variables & Data Types',
                        slug: 'c-variables',
                        contentFile: 'C/CVariables',
                        excerpt: 'Learn how to store and manage data in C. Master variable declaration, naming conventions, and the technical scope of variables in memory.',
                        keywords: ['c variables guide', 'declaring variables in c', 'c naming conventions', 'variable scope c', 'memory storage c']
                    },
                    {
                        id: 'c-constants',
                        title: 'Constants & Literals',
                        slug: 'c-constants',
                        contentFile: 'C/CConstants',
                        excerpt: 'Master the use of constants and literals in C. Learn about #define vs const and how to use them for safer, more maintainable software.',
                        keywords: ['c constants types', 'literals in c', 'define vs const c', 'read-only variables c', 'constant expressions c']
                    },
                    {
                        id: 'c-input-output',
                        title: 'Basic Input/Output (printf, scanf)',
                        slug: 'c-input-output',
                        contentFile: 'C/CInputOutput',
                        excerpt: 'Learn to bridge the gap between your program and the user. Master formatted input and output using the powerful printf and scanf functions.',
                        keywords: ['c input output', 'printf and scanf guide', 'formatted io c', 'std i/o library', 'user input c']
                    }
                ]
            },
            {
                id: 'c-operators',
                title: '4. Operators & Expressions',
                lessons: [
                    {
                        id: 'c-arithmetic-operators',
                        title: 'Arithmetic Operators',
                        slug: 'c-arithmetic-operators',
                        contentFile: 'C/CArithmeticOperators',
                        excerpt: 'Master mathematical precision in C. Learn arithmetic operators, precedence rules, and how to perform complex calculations efficiently.',
                        keywords: ['c arithmetic operators', 'math in c', 'operator precedence c', 'mathematical expressions c', 'modulo operator c']
                    },
                    {
                        id: 'c-relational-logical',
                        title: 'Relational & Logical Operators',
                        slug: 'c-relational-logical',
                        contentFile: 'C/CRelationalOperators',
                        excerpt: 'Master decision-making logic. Learn relational and logical operators to build sophisticated boolean conditions and control program flow.',
                        keywords: ['c logical operators', 'relational operators c', 'boolean logic in c', 'conditional expressions c', 'and or not c']
                    },
                    {
                        id: 'c-bitwise-operators',
                        title: 'Bitwise Operators',
                        slug: 'c-bitwise-operators',
                        contentFile: 'C/CBitwiseOperators',
                        excerpt: 'Master the power of bit manipulation. Learn AND, OR, XOR, and shift operators for high-performance low-level programming and flag management.',
                        keywords: ['c bitwise operators', 'bit manipulation c', 'low level programming c', 'shift operators c', 'manipulating bits']
                    },
                    {
                        id: 'c-type-casting',
                        title: 'Assignment & Type Casting',
                        slug: 'c-type-casting',
                        contentFile: 'C/CTypeCasting',
                        excerpt: 'Understand how C converts data types. Master implicit coercion and explicit type casting to prevent precision loss and bugs in your calculations.',
                        keywords: ['c type casting', 'data type conversion c', 'implicit vs explicit casting', 'type coercion c', 'precision management c']
                    }
                ]
            },
            {
                id: 'c-control-flow',
                title: '5. Control Flow',
                lessons: [
                    {
                        id: 'c-if-else',
                        title: 'If-Else & Nested If',
                        slug: 'c-if-else',
                        contentFile: 'C/CIfElse',
                        excerpt: 'Master conditional branching in C. Learn to build complex logic trees using if, else if, and else for responsive and smart applications.',
                        keywords: ['c if else statement', 'conditional logic c', 'branching in programming', 'nested if statements', 'decision making c']
                    },
                    {
                        id: 'c-loops',
                        title: 'Loops (for, while, do-while)',
                        slug: 'c-loops',
                        contentFile: 'C/CLoops',
                        excerpt: 'Master repetitive tasks. Learn the technical differences between for, while, and do-while loops and when to use each for optimal efficiency.',
                        keywords: ['c loops tutorial', 'for vs while loop c', 'do while usage', 'iteration in c', 'loop optimization c']
                    },
                    {
                        id: 'c-switch',
                        title: 'Switch-Case Statement',
                        slug: 'c-switch',
                        contentFile: 'C/CSwitch',
                        excerpt: 'Simplify complex multi-way branching. Master the switch-case statement and learn best practices for building clean, readable selection logic.',
                        keywords: ['c switch case', 'multi-way branching c', 'switch vs if else', 'default case usage', 'cleaning up logic c']
                    },
                    {
                        id: 'c-goto',
                        title: 'Break, Continue, and Goto',
                        slug: 'c-goto',
                        contentFile: 'C/CGoto',
                        excerpt: 'Master flow control interrupts. Learn when and how (or why not) to use break, continue, and the controversial goto statement in C.',
                        keywords: ['c break continue', 'goto statement c', 'loop control c', 'programming flow c', 'exiting loops c']
                    }
                ]
            },
            {
                id: 'c-functions',
                title: '6. Functions',
                lessons: [
                    {
                        id: 'c-functions-basics',
                        title: 'Function Declaration & Definition',
                        slug: 'c-functions-basics',
                        contentFile: 'C/CFunctionsBasics',
                        excerpt: 'Master the building blocks of C logic. Learn function declaration, definition, and how to pass arguments for reusable and modular engineering.',
                        keywords: ['c functions tutorial', 'declaring functions c', 'function parameters c', 'return values c', 'modular programming c']
                    },
                    {
                        id: 'c-scope-storage',
                        title: 'Scope, Storage Classes & Lifetime',
                        slug: 'c-scope-storage',
                        contentFile: 'C/CScopeStorage',
                        excerpt: 'Understand where your data lives and how long it lasts. Master scope, lifetime, and storage classes like static, auto, register, and extern.',
                        keywords: ['c storage classes', 'variable scope c', 'static variables c', 'lifetime in c', 'extern keyword c']
                    },
                    {
                        id: 'c-recursion',
                        title: 'Recursion',
                        slug: 'c-recursion',
                        contentFile: 'C/CRecursion',
                        excerpt: 'Master the power of functions calling themselves. Learn to solve complex nested problems and understand the call stack mechanics to prevent overflows.',
                        keywords: ['c recursion guide', 'recursive functions c', 'divide and conquer c', 'stack overflow c', 'factorial recursion c']
                    },
                    {
                        id: 'c-function-pointers-ch6',
                        title: 'Function Pointers',
                        slug: 'c-function-pointers-ch6',
                        contentFile: 'C/CFunctionPointers',
                        excerpt: 'Unlock advanced C flexibility. Learn to store function addresses in pointers to implement callbacks, dynamic dispatch, and high-level architectural patterns.',
                        keywords: ['c function pointers', 'callbacks in c', 'dynamic dispatch c', 'pointer to function', 'advanced c architecture']
                    }
                ]
            },
            {
                id: 'c-arrays',
                title: '7. Arrays',
                lessons: [
                    {
                        id: 'c-arrays-basics',
                        title: 'Declaring & Initializing Arrays',
                        slug: 'c-arrays-basics',
                        contentFile: 'C/CArraysBasics',
                        excerpt: 'Master contiguous data storage. Learn to declare, initialize, and traverse one-dimensional arrays while understanding their memory representation.',
                        keywords: ['c arrays tutorial', 'declaring arrays c', 'array initialization c', 'traversing arrays c', 'memory layout arrays']
                    },
                    {
                        id: 'c-multidimensional-arrays',
                        title: 'Multidimensional Arrays',
                        slug: 'c-multidimensional-arrays',
                        contentFile: 'C/CMultidimensionalArrays',
                        excerpt: 'Go beyond linear data. Master 2D and 3D arrays for representing grids, matrices, and complex spatial data structures in C.',
                        keywords: ['c 2d arrays', 'multidimensional arrays c', 'matrix operations c', 'nested loops arrays', 'grid data storage c']
                    },
                    {
                        id: 'c-array-algorithms',
                        title: 'Array Traversal & Algorithms',
                        slug: 'c-array-algorithms',
                        contentFile: 'C/CArrayAlgorithms',
                        excerpt: 'Master the fundamental logic of data processing. Learn efficient search and sort algorithms (Bubble, Selection, Linear) implemented in pure C.',
                        keywords: ['c array algorithms', 'sorting in c', 'searching arrays c', 'bubble sort c', 'selection sort c']
                    },
                    {
                        id: 'c-arrays-pointers',
                        title: 'Arrays and Pointers',
                        slug: 'c-arrays-pointers',
                        contentFile: 'C/CArraysPointers',
                        excerpt: 'Understand the deep relationship between arrays and pointers. Learn how array names decay into pointers and how to navigate data with pointer arithmetic.',
                        keywords: ['c arrays and pointers', 'array decay c', 'pointer arithmetic c', 'accessing arrays with pointers', 'pointer to array c']
                    }
                ]
            },
            {
                id: 'c-pointers',
                title: '8. Pointers',
                lessons: [
                    {
                        id: 'c-pointers-basics',
                        title: 'Introduction to Pointers',
                        slug: 'c-pointers-basics',
                        contentFile: 'C/CPointersBasics',
                        excerpt: "Master C's most powerful and feared feature. Learn memory addresses, the address-of (&) and dereference (*) operators, and the NULL pointer safety net.",
                        keywords: ['c pointers basics', 'understanding pointers c', 'dereferencing pointers', 'memory addresses c', 'null pointer c']
                    },
                    {
                        id: 'c-pointer-arithmetic',
                        title: 'Pointer Arithmetic',
                        slug: 'c-pointer-arithmetic',
                        contentFile: 'C/CPointerArithmetic',
                        excerpt: 'Navigate memory with precision. Learn how pointers scale based on data types and how to perform arithmetic to move safely through buffers and arrays.',
                        keywords: ['c pointer arithmetic', 'moving pointers c', 'scale factor c', 'pointer offset c', 'safely navigating memory']
                    },
                    {
                        id: 'c-pointer-to-pointer',
                        title: 'Pointer to Pointer',
                        slug: 'c-pointer-to-pointer',
                        contentFile: 'C/CPointerToPointer',
                        excerpt: 'Master multiple levels of indirection. Learn why and how to use double pointers (int**), common in dynamic arrays and complex data structures.',
                        keywords: ['c double pointers', 'pointer to pointer', 'multiple indirection c', 'dynamic array of pointers', 'handle pointers c']
                    },
                    {
                        id: 'c-const-pointers',
                        title: 'Pointers and Const',
                        slug: 'c-const-pointers',
                        contentFile: 'C/CConstPointers',
                        excerpt: "Identify the difference between a pointer to a constant and a constant pointer. Master 'const' placement to build robust and immutable memory interfaces.",
                        keywords: ['c const pointers', 'constant pointer c', 'pointer to const c', 'robust memory c', 'immutable pointers c']
                    },
                    {
                        id: 'c-void-pointers',
                        title: 'Void Pointers',
                        slug: 'c-void-pointers',
                        contentFile: 'C/CVoidPointers',
                        excerpt: 'Master generic programming in C. Learn how void pointers (void*) allow you to point to any data type, essential for generic libraries and memory functions.',
                        keywords: ['c void pointers', 'generic pointers c', 'void* usage c', 'polymorphism in c', 'generic memory management']
                    }
                ]
            },
            {
                id: 'c-strings',
                title: '9. Strings',
                lessons: [
                    {
                        id: 'c-strings-basics',
                        title: 'C String Declaration and Basics',
                        slug: 'c-strings-basics',
                        contentFile: 'C/CStringsBasics',
                        excerpt: 'Master null-terminated character sequences. Learn how C handles text as character arrays and the importance of the null terminator (\\0) for string safety.',
                        keywords: ['c strings tutorial', 'character arrays c', 'null terminator c', 'declaring strings c', 'string memory layout']
                    },
                    {
                        id: 'c-string-functions',
                        title: 'Standard String Functions in C',
                        slug: 'c-string-functions',
                        contentFile: 'C/CStringFunctions',
                        excerpt: 'Master the standard string library (string.h). Learn to use strlen, strcpy, strcat, and strcmp for professional text manipulation and comparison.',
                        keywords: ['c string functions', 'string.h guide', 'strlen and strcpy', 'comparing strings c', 'safe string handling']
                    },
                    {
                        id: 'c-string-manipulation',
                        title: 'String Manipulation & Pointers',
                        slug: 'c-string-manipulation',
                        contentFile: 'C/CStringManipulation',
                        excerpt: 'Deep dive into advanced text processing. Learn to manipulate strings using direct pointer operations for maximum performance and fine-grained control.',
                        keywords: ['c string manipulation', 'pointers and strings', 'custom string functions', 'tokenizing strings c', 'text processing c']
                    }
                ]
            },
            {
                id: 'c-structures',
                title: '10. Structures & Unions',
                lessons: [
                    {
                        id: 'c-structures',
                        title: 'Defining & Using Structures',
                        slug: 'c-structures',
                        contentFile: 'C/CStructures',
                        excerpt: "Master custom data types. Learn to group different data types into single 'structs' to model complex real-world entities like users, products, or nodes.",
                        keywords: ['c structures guide', 'declaring structs c', 'struct members c', 'nested structures c', 'memory padding structs']
                    },
                    {
                        id: 'c-unions',
                        title: 'Unions & Enums',
                        slug: 'c-unions',
                        contentFile: 'C/CUnions',
                        excerpt: 'Master memory-efficient data storage. Learn to use unions to share the same memory space for different types and how to use enums for readable constants.',
                        keywords: ['c unions tutorial', 'unions vs structs', 'memory efficiency c', 'shared memory types', 'tagged unions c']
                    },
                    {
                        id: 'c-bit-fields',
                        title: 'Bit Fields',
                        slug: 'c-bit-fields',
                        contentFile: 'C/CBitFields',
                        excerpt: 'Master hardware-level data grouping. Learn to use bit fields to pack multiple flags into single bytes for low-level memory optimization and protocol design.',
                        keywords: ['c bit fields', 'packing data c', 'memory optimization c', 'multiple flags byte', 'hardware level c']
                    },
                    {
                        id: 'c-enumerations',
                        title: 'Enumerations',
                        slug: 'c-enumerations',
                        contentFile: 'C/CEnumerations',
                        excerpt: "Master symbolic constants. Learn to use 'enum' to create readable, type-safe sets of related integers for better code clarity and maintainability.",
                        keywords: ['c enums guide', 'enumerations c', 'symbolic constants c', 'readable switch cases', 'enum vs define c']
                    }
                ]
            },
            {
                id: 'c-dynamic-memory',
                title: '11. Dynamic Memory Management',
                lessons: [
                    {
                        id: 'c-malloc-free',
                        title: 'malloc() and free()',
                        slug: 'c-malloc-free',
                        contentFile: 'C/CMallocFree',
                        excerpt: 'Master manual memory control. Learn to use malloc() to request heap memory and the critical importance of free() to prevent leaks and system exhaustion.',
                        keywords: ['c malloc free', 'dynamic memory allocation c', 'heap vs stack c', 'manual memory management', 'memory allocation tutorial']
                    },
                    {
                        id: 'c-calloc-realloc',
                        title: 'calloc() and realloc()',
                        slug: 'c-calloc-realloc',
                        contentFile: 'C/CCallocRealloc',
                        excerpt: 'Extend your memory management skills. Learn to allocate zeroed memory with calloc() and resize existing allocations safely with realloc() for dynamic data.',
                        keywords: ['c calloc realloc', 'resizing memory c', 'dynamic arrays c', 'zeroing memory c', 'memory management best practices']
                    },
                    {
                        id: 'c-memory-leaks',
                        title: 'Memory Leaks & Best Practices',
                        slug: 'c-memory-leaks',
                        contentFile: 'C/CMemoryLeaks',
                        excerpt: 'Build robust, professional-grade software. Learn to identify and prevent memory leaks, dangling pointers, and double-free errors using modern debugging principles.',
                        keywords: ['c memory leaks', 'memory debugging', 'dangling pointers c', 'double free error', 'robust code c']
                    }
                ]
            },
            {
                id: 'c-file-io',
                title: '12. File Input/Output',
                lessons: [
                    {
                        id: 'c-file-operations',
                        title: 'Basic File Operations in C',
                        slug: 'c-file-operations',
                        contentFile: 'C/CFileOperations',
                        excerpt: 'Learn to interact with the file system. Master opening, reading, writing, and closing text files safely using the standard C file handling library (stdio.h).',
                        keywords: ['c file handling', 'reading files c', 'writing files c', 'fopen and fclose', 'text file I/O c']
                    },
                    {
                        id: 'c-binary-files',
                        title: 'Working with Binary Files',
                        slug: 'c-binary-files',
                        contentFile: 'C/CBinaryFiles',
                        excerpt: 'Master high-performance data storage. Learn to read and write binary data directly for efficient storage of structures, images, and custom data formats.',
                        keywords: ['c binary files', 'fread and fwrite', 'storing structs in files', 'binary data c', 'efficient file I/O']
                    },
                    {
                        id: 'c-file-errors',
                        title: 'Error Handling in File I/O',
                        slug: 'c-file-errors',
                        contentFile: 'C/CFileErrors',
                        excerpt: 'Build resilient file-based applications. Learn to handle errors during file operations, check for end-of-file (EOF), and use perror() for informative debugging.',
                        keywords: ['c file error handling', 'perror usage c', 'eof checking c', 'resilient file I/O', 'debugging file operations']
                    },
                    {
                        id: 'c-advanced-file-io',
                        title: 'Random Access & Buffering',
                        slug: 'c-advanced-file-io',
                        contentFile: 'C/CAdvancedFileIO',
                        excerpt: 'Master professional file control. Learn random access with fseek() and ftell(), and understand how buffering works to optimize your program’s Disk I/O.',
                        keywords: ['c advanced file io', 'fseek and ftell', 'random access files', 'file buffering c', 'performance file handling']
                    }
                ]
            },
            {
                id: 'c-preprocessor',
                title: '13. Preprocessor Directives',
                lessons: [
                    {
                        id: 'c-preprocessor-basics',
                        title: 'Macros & Conditional Compilation',
                        slug: 'c-preprocessor-basics',
                        contentFile: 'C/CPreprocessorBasics',
                        excerpt: 'Understand the C compilation precursor. Learn to use #define macros, conditional compilation (#ifdef), and understand the power of constants and code toggles.',
                        keywords: ['c preprocessor tutorial', 'macros in c', 'conditional compilation', 'ifdef usage', 'compile time constants']
                    },
                    {
                        id: 'c-advanced-macros',
                        title: 'Advanced Macro Techniques',
                        slug: 'c-advanced-macros',
                        contentFile: 'C/CAdvancedMacros',
                        excerpt: 'Unlock advanced metaprogramming. Learn function-like macros, stringification (#), token pasting (##), and best practices for building complex build-time logic.',
                        keywords: ['c advanced macros', 'function-like macros', 'macro stringification', 'token pasting c', 'metaprogramming in c']
                    },
                    {
                        id: 'c-header-files',
                        title: 'Header Files & Include Guards',
                        slug: 'c-header-files',
                        contentFile: 'C/CHeaderFiles',
                        excerpt: 'Master professional project organization. Learn to design clean header files (.h), use include guards, and manage dependencies across multiple C source files.',
                        keywords: ['c header files', 'include guards', 'modular c design', 'project structure c', 'source file organization']
                    },
                    {
                        id: 'c-pragma-directives',
                        title: 'Pragma Directives',
                        slug: 'c-pragma-directives',
                        contentFile: 'C/CPragmaDirectives',
                        excerpt: 'Master compiler-specific controls. Learn to use #pragma for controlling warnings, packing structures, and other specialized instructions for modern compilers.',
                        keywords: ['c pragma directives', 'compiler instructions', 'structure packing pragma', 'suppressing warnings c', 'gcc pragma guide']
                    }
                ]
            },
            {
                id: 'c-advanced',
                title: '14. Advanced Topics',
                lessons: [
                    {
                        id: 'c-variadic-functions',
                        title: 'Variadic Functions (stdarg.h)',
                        slug: 'c-variadic-functions',
                        contentFile: 'C/CVariadicFunctions',
                        excerpt: 'Master flexible function signatures. Learn to build functions like printf that accept any number of arguments using the stdarg.h library.',
                        keywords: ['c variadic functions', 'stdarg.h guide', 'variable arguments c', 'printf implementation', 'va_list usage']
                    },
                    {
                        id: 'c-inline-assembly',
                        title: 'Inline Assembly (GCC Specific)',
                        slug: 'c-inline-assembly',
                        contentFile: 'C/CInlineAssembly',
                        excerpt: 'Unlock the ultimate level of performance. Learn to embed assembly instructions directly within your C code for hardware-level control and micro-optimizations.',
                        keywords: ['c inline assembly', 'gcc inline asm', 'low level optimization', 'assembly in c', 'hardware level programming']
                    },
                    {
                        id: 'c-linking',
                        title: 'Static vs. Dynamic Linking',
                        slug: 'c-linking',
                        contentFile: 'C/CLinking',
                        excerpt: 'Master the final stage of building software. Understand static vs dynamic linking, library creation (.a/.so/.lib), and how to manage external dependencies.',
                        keywords: ['c linking process', 'static vs dynamic linking', 'creating libraries c', 'object files and linking', 'binary compilation c']
                    }
                ]
            },
            {
                id: 'c-best-practices',
                title: '15. Best Practices & Beyond',
                lessons: [
                    {
                        id: 'c-coding-standards',
                        title: 'Coding Standards (MISRA C, CERT C)',
                        slug: 'c-coding-standards',
                        contentFile: 'C/CCodingStandards',
                        excerpt: 'Learn the rules of industrial-grade C. Explore professional coding standards like MISRA C and CERT C for safety-critical and secure software engineering.',
                        keywords: ['c coding standards', 'misra c tutorial', 'secure c programming', 'cert c standards', 'professional c code']
                    },
                    {
                        id: 'c-debugging',
                        title: 'Debugging Techniques in C',
                        slug: 'c-debugging',
                        contentFile: 'C/CDebugging',
                        excerpt: 'Master the art of troubleshooting. Learn to use GDB, Valgrind, and address sanitizers to find and fix the most complex memory and logic bugs in C.',
                        keywords: ['c debugging techniques', 'using gdb', 'valgrind tutorial', 'memory leak debugging', 'troubleshooting c code']
                    },
                    {
                        id: 'c-common-pitfalls',
                        title: 'Common C Pitfalls & How to Avoid Them',
                        slug: 'c-common-pitfalls',
                        contentFile: 'C/CCommonPitfalls',
                        excerpt: "Avoid the mistakes that plague beginners and pros alike. Learn about undefined behavior, buffer overflows, and other common 'foot-guns' in C programming.",
                        keywords: ['c common mistakes', 'understanding undefined behavior', 'buffer overflow prevention', 'c traps and pitfalls', 'safe c code']
                    },
                    {
                        id: 'c-optimization',
                        title: 'Code Optimization Techniques',
                        slug: 'c-optimization',
                        contentFile: 'C/COptimization',
                        excerpt: 'Write code that flies. Learn to optimize C for speed and memory usage, understanding cache friendliness, branch prediction, and compiler optimizations.',
                        keywords: ['c optimization techniques', 'performance tuning c', 'cache friendly code', 'writing fast c', 'compiler optimization flags']
                    },
                    {
                        id: 'c-future',
                        title: 'Modern C & Future of the Language',
                        slug: 'c-future',
                        contentFile: 'C/CFuture',
                        excerpt: "Discover what's next for the foundations of computing. Explore the C23 standard and the future roadmap of the language in a world of Rust and modern C++.",
                        keywords: ['future of c', 'c23 standard features', 'next gen c programming', 'c vs rust future', 'modern c development']
                    }
                ]
            }
        ]
    }
];

// Flattened for easy access and backward compatibility
export const tutorials: Course[] = [
    {
        id: 'python-basics-intro',
        title: 'Getting Started with Python: A Beginner\'s Guide',
        slug: 'getting-started-python-basics',
        language: 'python',
        date: 'December 21, 2025',
        readTime: 10,
        author: 'FileFusion Team',
        imageUrl: 'https://images.unsplash.com/photo-1526374965328-7f61d4dc18c5?auto=format&fit=crop&w=1000&q=80',
        excerpt: 'Learn the fundamentals of Python programming, from installation to writing your first script. Perfect for beginners looking to enter the professional world of coding.',
        keywords: ['python for beginners', 'python basics', 'learn python', 'python course 2026', 'programming fundamentals'],
        hasFullContent: true,
        contentFile: 'Python/PythonBasicsIntro',
        isLesson: true,
        courseId: 'python-complete-course'
    },
    {
        id: 'html-mastery-intro',
        title: 'Mastering HTML5: The Foundation of the Web',
        slug: 'html-overview',
        language: 'html',
        date: 'December 22, 2025',
        readTime: 8,
        author: 'FileFusion Team',
        imageUrl: 'https://images.unsplash.com/photo-1542831371-29b0f74f9713?auto=format&fit=crop&w=1000&q=80',
        excerpt: 'Dive into the world of web development by mastering HTML5. Learn how to structure pages, work with semantic tags, and build accessible, production-ready sites.',
        keywords: ['html5 course', 'web development basics', 'learn html', 'semantic html', 'coding for beginners'],
        hasFullContent: true,
        contentFile: 'HTML/HTMLOverview',
        isLesson: true,
        courseId: 'html-mastery-course'
    },
    {
        id: 'js-modern-intro',
        title: 'Modern JavaScript: ES6+ and Beyond',
        slug: 'javascript-introduction',
        language: 'javascript',
        date: 'December 22, 2025',
        readTime: 12,
        author: 'FileFusion Team',
        imageUrl: 'https://images.unsplash.com/photo-1579468118864-1b9ea3c0db4a?auto=format&fit=crop&w=1000&q=80',
        excerpt: 'Unlock the power of the web with modern JavaScript. From variable scoping to asynchronous patterns, learn why JS is the core language of the modern era.',
        keywords: ['javascript course', 'es6 basics', 'learn js', 'modern javascript', 'web programming'],
        hasFullContent: true,
        contentFile: 'JS/JSIntroduction',
        isLesson: true,
        courseId: 'js-modern-course'
    },
    {
        id: 'c-modern-intro',
        title: 'C Programming: The Foundation of Engineering',
        slug: 'c-programming-intro',
        language: 'c',
        date: 'December 23, 2025',
        readTime: 15,
        author: 'FileFusion Team',
        imageUrl: 'https://images.unsplash.com/photo-1515879218367-8466d910aaa4?auto=format&fit=crop&w=1000&q=80',
        excerpt: 'Master the foundation of modern computing. Learn why C remains the core of operating systems, embedded hardware, and high-performance software engineering.',
        keywords: ['c programming course', 'learn c language', 'systems programming', 'low level engineering', 'c tutorials 2026'],
        hasFullContent: true,
        contentFile: 'C/COverview',
        isLesson: true,
        courseId: 'c-fundamentals-course'
    }
];

export const getLessonBySlug = (courseId: string, slug: string): Lesson | null => {
    const course = courses.find(c => c.id === courseId);
    if (!course) return null;

    for (const chapter of course.chapters) {
        const lesson = chapter.lessons.find(l => l.slug === slug);
        if (lesson) return { ...lesson, courseTitle: course.title, chapters: course.chapters };
    }
    return null;
};
